import React, { useState, useCallback, useEffect, useRef } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  IconButton,
  List,
  ListItem,
  Stack,
  Tab,
  Tabs,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  RadioButtonUnchecked as CircleIcon,
  Clear as ClearIcon,
  Search as SearchIcon,
  Delete as DeleteIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  Download as DownloadIcon,
  PictureAsPdf as PdfIcon,
  LocationOn,
  Home,
  BarChart,
  Groups,
  LaptopMac,
  Lightbulb,
  School,
  Timer,
  Wifi as WifiIcon,
  Bluetooth as BluetoothIcon,
  CellTower as CellTowerIcon,
  SatelliteAlt as GnssIcon,
  Sensors as SignalsIcon,
  CompareArrows as CompareIcon,
  BookmarkAdd as BookmarkAddIcon,
  Bookmark as BookmarkIcon,
  BookmarkBorder as BookmarkBorderIcon,
  Check as CheckIcon,
  Close as CloseIcon,
  MyLocation as MyLocationIcon,
  Pentagon as PentagonIcon,
  FactCheck as FactCheckIcon,
} from '@mui/icons-material';
import PromoteToPacketDialog, { PromoteTarget } from '@signals/app-shell/components/intel/PromoteToPacketDialog';
import { useFeatureFlagsStore } from '@signals/app-shell/stores/featureFlagsStore';
import pathfinderApi, { PathfinderApiService, PathfinderDataPoint, SignalEnvironmentData, SignalCompareResult, AdtechAreaSearchResult, SavedArea } from '../../services/pathfinderApi';
import { parseBackendDateTime } from '@signals/app-shell/utils/parseDateTime';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import SavedDevicesPanel from './SavedDevicesPanel';
import AdtechDeviceActions from './AdtechDeviceActions';

/**
 * Ray-casting point-in-polygon test. `ring` is a GeoJSON outer ring of
 * [lon, lat] pairs (open or closed); `lon`/`lat` are the test point. Used to
 * precisely clip radius-fetched results down to a freeform polygon area.
 */
const pointInRing = (lon: number, lat: number, ring: [number, number][]): boolean => {
  let inside = false;
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const xi = ring[i][0], yi = ring[i][1];
    const xj = ring[j][0], yj = ring[j][1];
    const intersect =
      (yi > lat) !== (yj > lat) &&
      lon < ((xj - xi) * (lat - yi)) / ((yj - yi) || Number.EPSILON) + xi;
    if (intersect) inside = !inside;
  }
  return inside;
};

export interface DrawCircle {
  id: string;
  center: [number, number]; // [lat, lon]
  radius: number; // meters
  name: string;
  visible: boolean;
  /**
   * Optional polygon search area (GeoJSON outer ring: [lon, lat] pairs). When
   * present the area is a freeform polygon, not a circle: `center`/`radius`
   * hold its bounding circle (used for the radius-based backend queries) and
   * results are precisely clipped to this ring. Rendered as the actual polygon.
   */
  polygon?: [number, number][];
  results?: AreaSearchResult[];
  loading?: boolean;
  dwellInsights?: DwellInsight[];
  coLocationInsights?: CoLocationInsight[];
  signalEnvironment?: SignalEnvironmentData | null;
  adtechData?: AdtechAreaSearchResult | null;
  aiSummary?: string | null;
  mapboxContext?: any; // Geographic context from Mapbox
}

export interface AreaSearchResult {
  entityId: string;
  entityType: string;
  deviceType: string;
  pointCount: number;
  timeRange: {
    start: Date;
    end: Date;
  };
  confidence: number;
  dataPoints?: PathfinderDataPoint[]; // Store the actual points from area search
}

export interface DwellInsight {
  entityId: string;
  deviceType: string;
  dwellCount: number;
  totalDwellTime: number; // seconds
  // All dwell intervals for this entity/device within the circle (used for co-location detection)
  dwells?: Array<{
    startTime: Date;
    endTime: Date;
    latitude: number;
    longitude: number;
    durationSeconds?: number;
    placeName?: string;
    placeType?: string;
  }>;
  longestDwell: {
    duration: number; // seconds
    startTime: Date;
    endTime: Date;
    placeName?: string;
    placeType?: string;
  };
  coordinates: [number, number];
  placeName?: string;  // Location name from geocoding
  placeType?: string;  // Location category (cafe, university, etc.)
}

export interface CoLocationInsight {
  entities: string[]; // List of entity IDs that were co-located
  deviceTypes: string[]; // Device types involved
  overlapStart: Date;
  overlapEnd: Date;
  duration: number; // seconds of overlap
  location: [number, number]; // Approximate center
  confidence: number; // 0-1, based on how close they were
  placeName?: string;  // Location name where co-location occurred
  placeType?: string;  // Location category
}

interface AreaSearchProps {
  selectedStudents: string[];
  selectedCourse?: string;
  dateRange: { start: Date | null; end: Date | null };
  queryTimeZone?: string;
  timeRange?: { start: Date; end: Date } | null;
  onDrawModeChange: (enabled: boolean) => void;
  onCircleAdd: (circle: DrawCircle) => void;
  onCircleUpdate: (circle: DrawCircle) => void;
  onCircleDelete: (circleId: string) => void;
  drawCircles: DrawCircle[];
  isDrawingMode: boolean;
  defaultRadius?: number;
  onRadiusChange?: (radius: number) => void;
  onLoadAreaData?: (dataPoints: PathfinderDataPoint[], circleId: string) => void;
  onLoadAreaDwells?: (dwells: any[], circleId: string) => void;
  /** When true, area analysis also queries AdTech (Venntel) data for the circle. */
  adtechEnabled?: boolean;
  /** Plot the circle's AdTech signals on the map + open the floating results panel. */
  onLoadAdtechToMap?: (
    signals: Array<{
      registration_id: string;
      latitude: number;
      longitude: number;
      timestamp?: string | null;
      carrier?: string | null;
      country?: string | null;
      accuracy?: number | null;
      ip_address?: string | null;
      timezone?: string | null;
      forensic_flag?: string | null;
    }>,
    label?: string,
  ) => void;
  /** Load a saved-devices selection into the main student picker + fetch data. */
  onLoadSavedDevices?: (studentIds: string[]) => void;
  /** Load full pattern-of-life for saved AdTech registration ids. */
  onLoadAdtechPol?: (registrationIds: string[]) => void;
  /** True while an AdTech PoL load is in flight. */
  adtechPolLoading?: boolean;
  /** Full list of selectable students (for the Saved Devices sub-tab). */
  students?: Array<{ id: string; name: string }>;
  /** True while the map is in freeform polygon-draw mode (full area analysis). */
  isDrawingAdtechPolygon?: boolean;
  /** True while a drawn-polygon search is running (optional spinner state). */
  adtechPolygonLoading?: boolean;
  /** Toggle freeform polygon-draw mode on the map (draws a search-area polygon). */
  onDrawAdtechPolygonModeChange?: (enabled: boolean) => void;
  /** Look up AdTech signals for specific IP address(es) and plot them as tracks. */
  onLookupAdtechIps?: (ipAddresses: string[]) => void;
}
// (adtech polygon-search + saved-devices sub-tab props above)

const AreaSearch: React.FC<AreaSearchProps> = ({
  selectedStudents: _selectedStudents,
  selectedCourse,
  dateRange,
  queryTimeZone,
  timeRange,
  onDrawModeChange,
  onCircleAdd,
  onCircleUpdate,
  onCircleDelete,
  drawCircles,
  isDrawingMode,
  defaultRadius = 100,
  onRadiusChange,
  onLoadAreaData,
  onLoadAreaDwells,
  adtechEnabled = false,
  onLoadAdtechToMap,
  onLoadSavedDevices,
  onLoadAdtechPol,
  adtechPolLoading = false,
  students,
  isDrawingAdtechPolygon = false,
  adtechPolygonLoading = false,
  onDrawAdtechPolygonModeChange,
  onLookupAdtechIps,
}) => {
  // Top-level sub-tab: 0 = Search (draw + analyze), 1 = Saved Devices (watchlist).
  const [mainTab, setMainTab] = useState(0);
  // ID / IP direct lookup (AdTech): free-text of registration ids and/or IPs.
  const [lookupText, setLookupText] = useState('');
  // Per-circle AdTech device-count preview: circleId -> result | 'loading' | error.
  const [areaPreview, setAreaPreview] = useState<Record<string, { loading?: boolean; devices?: number; signals?: number; estimated?: boolean; error?: string }>>({});

  const IPV4_RE = /^\d{1,3}\.\d{1,3}\.\d{1,3}\.\d{1,3}$/;

  // Split free-text into registration ids vs IPs, then dispatch: IDs reuse the
  // pattern-of-life loader, IPs go through the dedicated IP-lookup path. Both
  // plot discovered devices as tracks on the map + device tree.
  const handleIdIpLookup = useCallback(() => {
    const tokens = lookupText
      .split(/[\s,;]+/)
      .map((t) => t.trim())
      .filter(Boolean);
    if (tokens.length === 0) return;
    const ips = tokens.filter((t) => IPV4_RE.test(t));
    const ids = tokens.filter((t) => !IPV4_RE.test(t));
    if (ids.length > 0) onLoadAdtechPol?.(ids);
    if (ips.length > 0) onLookupAdtechIps?.(ips);
  }, [lookupText, onLoadAdtechPol, onLookupAdtechIps]);

  // Cheap device/signal count for a drawn area before committing to the full
  // (paid) pull. Uses the circle's exact polygon ring when present.
  const handlePreviewArea = useCallback(async (circle: DrawCircle) => {
    if (!dateRange.start || !dateRange.end) return;
    setAreaPreview((p) => ({ ...p, [circle.id]: { loading: true } }));
    try {
      const isPoly = Array.isArray(circle.polygon) && circle.polygon.length >= 3;
      const result = await pathfinderApi.queryAdtechDiscoverOnly({
        center: isPoly ? undefined : circle.center,
        radiusMeters: isPoly ? undefined : circle.radius,
        polygon: isPoly ? circle.polygon : undefined,
        dateRange: [dateRange.start, dateRange.end],
        timeZone: queryTimeZone,
      });
      setAreaPreview((p) => ({
        ...p,
        [circle.id]: {
          devices: result.unique_devices,
          signals: result.total_signals,
          estimated: result.estimated,
        },
      }));
    } catch (err: any) {
      setAreaPreview((p) => ({ ...p, [circle.id]: { error: err?.message || 'Preview failed' } }));
    }
  }, [dateRange.start, dateRange.end, queryTimeZone]);
  const [searchMode, _setSearchMode] = useState<'current-time' | 'date-range'>('current-time');
  const [circleSubTabs, setCircleSubTabs] = useState<Record<string, number>>({});
  const [useFullDetail, setUseFullDetail] = useState(false);
  const [studentsOnly, setStudentsOnly] = useState(false);
  const [circleErrors, setCircleErrors] = useState<Record<string, { dwells?: string }>>({});
  const [compareMode, setCompareMode] = useState<'intersection' | 'union'>('intersection');
  const [compareResults, setCompareResults] = useState<SignalCompareResult | null>(null);
  const [compareLoading, setCompareLoading] = useState(false);

  // ---- Saved Areas (persisted geofences) & Saved Devices (watchlist) ----
  const [savedAreas, setSavedAreas] = useState<SavedArea[]>([]);
  const [savedAreasOpen, setSavedAreasOpen] = useState(false);
  const [savedAreasLoading, setSavedAreasLoading] = useState(false);
  const [savingAreaId, setSavingAreaId] = useState<string | null>(null);
  const [areaNameDraft, setAreaNameDraft] = useState('');
  const [savedDeviceIds, setSavedDeviceIds] = useState<Set<string>>(new Set());
  const [savingDeviceId, setSavingDeviceId] = useState<string | null>(null);
  const intelEnabled = useFeatureFlagsStore((s) => s.intel);
  const [promoteTarget, setPromoteTarget] = useState<PromoteTarget | null>(null);
  const [promoteExtras, setPromoteExtras] = useState<PromoteTarget[]>([]);
  const [promoteName, setPromoteName] = useState<string | undefined>(undefined);

  const loadSavedAreas = useCallback(async () => {
    setSavedAreasLoading(true);
    try {
      setSavedAreas(await pathfinderApi.getSavedAreas(selectedCourse));
    } catch {
      // Non-fatal: saved areas are optional convenience.
    } finally {
      setSavedAreasLoading(false);
    }
  }, [selectedCourse]);

  useEffect(() => {
    loadSavedAreas();
  }, [loadSavedAreas]);

  // Load the set of already-saved device ids so we can show a filled bookmark.
  useEffect(() => {
    let cancelled = false;
    pathfinderApi
      .getSavedDevices(selectedCourse)
      .then((devs) => {
        if (cancelled) return;
        const ids = new Set<string>();
        for (const d of devs) ids.add(d.registration_id);
        setSavedDeviceIds(ids);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [selectedCourse]);

  // Begin saving a drawn circle: reveal a name field pre-filled with a sensible
  // default (the place label if geocoded, else the circle's own name).
  const beginSaveArea = useCallback((circle: DrawCircle) => {
    setSavingAreaId(circle.id);
    setAreaNameDraft(circle.mapboxContext?.place_name || circle.name || 'Saved Area');
  }, []);

  const commitSaveArea = useCallback(async (circle: DrawCircle) => {
    const name = areaNameDraft.trim();
    if (!name) return;
    try {
      await pathfinderApi.saveArea({
        name,
        center: circle.center,
        radiusMeters: circle.radius,
        courseId: selectedCourse,
        placeName: circle.mapboxContext?.place_name,
      });
      setSavingAreaId(null);
      setAreaNameDraft('');
      await loadSavedAreas();
      setSavedAreasOpen(true);
    } catch {
      // Surface minimally; keep the draft open so the user can retry.
    }
  }, [areaNameDraft, selectedCourse, loadSavedAreas]);

  // Reload a saved area as an editable Area Search circle.
  const loadSavedArea = useCallback((area: SavedArea) => {
    const { center, radius } = PathfinderApiService.savedAreaToCircle(area);
    const circle: DrawCircle = {
      id: `saved-${area.id}-${Date.now()}`,
      center,
      radius,
      name: area.name,
      visible: true,
      results: [],
      loading: false,
    };
    onCircleAdd(circle);
    onDrawModeChange(false);
  }, [onCircleAdd, onDrawModeChange]);

  const deleteSavedArea = useCallback(async (id: number) => {
    try {
      await pathfinderApi.deleteSavedArea(id);
      setSavedAreas((prev) => prev.filter((a) => a.id !== id));
    } catch {
      // ignore
    }
  }, []);

  // Save a device/entity found in an area to the watchlist ("Saved Devices").
  const saveDeviceToWatchlist = useCallback(async (entityId: string) => {
    if (!entityId || savedDeviceIds.has(entityId)) return;
    setSavingDeviceId(entityId);
    try {
      await pathfinderApi.saveDevice({
        registrationId: entityId,
        name: entityId,
        deviceKind: 'student',
        courseId: selectedCourse,
      });
      setSavedDeviceIds((prev) => new Set(prev).add(entityId));
      window.dispatchEvent(new Event('refresh-saved-devices'));
    } catch {
      // ignore
    } finally {
      setSavingDeviceId(null);
    }
  }, [savedDeviceIds, selectedCourse]);

  // Generate a unique name for new circles (flow drawing disabled)
  // const generateCircleName = useCallback(() => {
  //   const existingNames = drawCircles.map(c => c.name);
  //   let counter = 1;
  //   let name = `Area ${counter}`;
  //   while (existingNames.includes(name)) {
  //     counter++;
  //     name = `Area ${counter}`;
  //   }
  //   return name;
  // }, [drawCircles]);

  // Handle circle creation from map drawing (currently unused)
  // const handleCircleDrawn = useCallback((center: [number, number], radius: number) => {
  //   const clampedRadius = Math.min(radius, 2500);
  //   if (radius > 2500) {
  //     console.warn(`Radius ${radius}m exceeds maximum. Clamping to 2500m for performance`);
  //   }
  //   const newCircle: DrawCircle = {
  //     id: `circle-${Date.now()}`,
  //     center,
  //     radius: clampedRadius,
  //     name: generateCircleName(),
  //     visible: true,
  //     results: [],
  //     loading: false,
  //   };
  //   onCircleAdd(newCircle);
  //   onDrawModeChange(false);
  // }, [generateCircleName, onCircleAdd, onDrawModeChange]);

  /* Perform area search for a specific circle (currently unused)
  const performAreaSearch = useCallback(async (circle: DrawCircle) => {
    if (!dateRange.start || !dateRange.end) {
      console.warn('Date range not selected for area search');
      return;
    }

    // Update circle to show loading state
    const updatedCircle = { ...circle, loading: true };
    onCircleUpdate(updatedCircle);

    try {
      // Determine time range based on search mode
      let searchStartDate: Date;
      let searchEndDate: Date;
      
      if (searchMode === 'current-time' && timeRange) {
        // Use current time slider window with some buffer (e.g., ±30 minutes)
        const bufferMinutes = 30;
        searchStartDate = new Date(timeRange.start.getTime() - (bufferMinutes * 60 * 1000));
        searchEndDate = new Date(timeRange.end.getTime() + (bufferMinutes * 60 * 1000));
      } else {
        // Use full date range
        searchStartDate = dateRange.start;
        searchEndDate = dateRange.end;
      }

        // Query all entities within the circle during the time period
        const response = await pathfinderApi.queryAreaSearch({
          center: circle.center,
          radius: circle.radius,
          dateRange: [searchStartDate, searchEndDate],
          selectedCourse,
          excludeStudentIds: [],
        });

      if (!response || !Array.isArray(response)) {
        throw new Error('Invalid response format from area search API');
      }

      // Process results to create AreaSearchResult objects
      const results: AreaSearchResult[] = [];
      const entityGroups = new Map<string, PathfinderDataPoint[]>();
      
      // Group points by entity AND device type (like the device tree)
      response.forEach((point) => {
        const studentId = point.studentId || 'Unknown';
        const deviceType = point.deviceType || 'phone';
        // Create a unique key combining entity and device type
        const entityDeviceKey = `${studentId}-${deviceType}`;
        
        if (!entityGroups.has(entityDeviceKey)) {
          entityGroups.set(entityDeviceKey, []);
        }
        entityGroups.get(entityDeviceKey)!.push(point);
      });

      // Create results for each entity-device combination
      entityGroups.forEach((points, entityDeviceKey) => {
        if (points.length > 0) {
          // Extract entity ID and device type from the key
          const lastDashIndex = entityDeviceKey.lastIndexOf('-');
          const entityId = entityDeviceKey.substring(0, lastDashIndex);
          const deviceType = entityDeviceKey.substring(lastDashIndex + 1);
          
          // Calculate time range without spread operator to avoid stack overflow on large datasets
          let minTs = Infinity, maxTs = -Infinity;
          points.forEach(p => {
            const ts = p.timestamp.getTime();
            if (ts < minTs) minTs = ts;
            if (ts > maxTs) maxTs = ts;
          });
          
          const entityType = entityId.startsWith('SRP') ? 'SRP' : 
                           entityId.startsWith('RP') ? 'RP' : 
                           entityId.startsWith('Student') ? 'Student' : 'Unknown';
          
          const result = {
            entityId: `${entityId} - ${deviceType}`, // Display as "Student-02 - vehicle"
            entityType,
            deviceType: deviceType,
            pointCount: points.length,
            timeRange: {
              start: new Date(minTs),
              end: new Date(maxTs),
            },
            confidence: Math.min(1.0, points.length / 10), // Simple confidence based on point count
            dataPoints: points // Store the actual points for this entity
          };
          results.push(result);
        }
      });

      // Update circle with results
      const completedCircle = { 
        ...circle, 
        loading: false, 
        results: results.sort((a, b) => b.pointCount - a.pointCount) // Sort by point count
      };
      
      onCircleUpdate(completedCircle);

    } catch (error) {
      console.error('Area Search failed:', error);
      // Update circle to show error state
      const errorCircle = { ...circle, loading: false, results: [] };
      onCircleUpdate(errorCircle);
    }
  }, [dateRange, searchMode, timeRange, selectedCourse, selectedStudents, studentsOnly, onCircleUpdate]); */

  // Analyze dwells in the area
  const analyzeDwells = useCallback(async (circle: DrawCircle) => {
    if (!dateRange.start || !dateRange.end) return;

    try {
      // Fetch dwells for the area
      // Backend will filter by entity_type if studentsOnly=true
      const dwells = await pathfinderApi.getDwells({
        courseId: selectedCourse,
        startDate: dateRange.start,
        endDate: dateRange.end,
        timeZone: queryTimeZone,
        // Use server-side spatial filtering so we don't miss in-area dwells due to global limits
        center: circle.center,
        radiusMeters: circle.radius,
        limit: 50000,  // High limit, but now scoped to area so it stays reasonable
        studentsOnly: studentsOnly  // Backend filters by entity_type='Student'
      });

      // Clear any prior dwell error for this circle
      setCircleErrors(prev => {
        if (!prev[circle.id]?.dwells) return prev;
        const next = { ...prev };
        next[circle.id] = { ...next[circle.id], dwells: undefined };
        return next;
      });
      
      // Backend already filtered - no need for frontend filtering
      const filteredDwells = dwells;
      
      // Server spatial-filtered to the (bounding) circle. For polygon areas,
      // clip precisely to the drawn ring so dwells match the actual shape.
      const polyRing = circle.polygon;
      const dwellsInArea = (Array.isArray(polyRing) && polyRing.length >= 3)
        ? filteredDwells.filter((d: any) =>
            d?.longitude != null && d?.latitude != null &&
            pointInRing(Number(d.longitude), Number(d.latitude), polyRing))
        : filteredDwells;

      if (dwellsInArea.length === 0) {
        console.warn('[AreaSearch] No dwells found in area', {
          center: circle.center,
          radiusMeters: circle.radius,
          courseId: selectedCourse,
          start: dateRange.start?.toISOString(),
          end: dateRange.end?.toISOString(),
          studentsOnly
        });
      }

      // Group dwells by entity
      const dwellsByEntity = new Map<string, any[]>();
      dwellsInArea.forEach((dwell: any) => {
        const key = `${dwell.entity_id}-${dwell.device_type}`;
        if (!dwellsByEntity.has(key)) {
          dwellsByEntity.set(key, []);
        }
        dwellsByEntity.get(key)!.push(dwell);
      });

      // Calculate dwell insights
      const dwellInsights: DwellInsight[] = [];
      dwellsByEntity.forEach((dwells, entityKey) => {
        const totalDwellTime = dwells.reduce((sum, d) => sum + (d.duration_seconds || 0), 0);
        const longestDwell = dwells.reduce((max, d) => 
          (d.duration_seconds || 0) > (max?.duration_seconds || 0) ? d : max
        , dwells[0]);

        // Split by last dash to separate entity ID from device type
        // e.g., "Student-06-phone" -> "Student-06" and "phone"
        const lastDashIndex = entityKey.lastIndexOf('-');
        const entityId = entityKey.substring(0, lastDashIndex);
        const deviceType = entityKey.substring(lastDashIndex + 1);

        const dwellIntervals: NonNullable<DwellInsight['dwells']> = (dwells || [])
          .map((d: any) => {
            const start = parseBackendDateTime(d.start_time) ?? new Date(d.start_time);
            const end = parseBackendDateTime(d.end_time) ?? new Date(d.end_time);
            if (!Number.isFinite(start.getTime()) || !Number.isFinite(end.getTime())) return null;

            const latitude = Number(d.latitude);
            const longitude = Number(d.longitude);
            if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) return null;

            const durationSecondsRaw = Number(d.duration_seconds);
            const durationSeconds = Number.isFinite(durationSecondsRaw) ? durationSecondsRaw : undefined;

            return {
              startTime: start,
              endTime: end,
              latitude,
              longitude,
              durationSeconds,
              placeName: typeof d.place_name === 'string' ? d.place_name : undefined,
              placeType: typeof d.place_type === 'string' ? d.place_type : undefined
            };
          })
          .filter((x): x is NonNullable<typeof x> => x !== null)
          .sort((a, b) => a.startTime.getTime() - b.startTime.getTime());
        
        dwellInsights.push({
          entityId,
          deviceType: deviceType || 'phone',
          dwellCount: dwells.length,
          totalDwellTime,
          dwells: dwellIntervals,
          longestDwell: {
            duration: longestDwell.duration_seconds || 0,
            startTime: parseBackendDateTime(longestDwell.start_time) ?? new Date(longestDwell.start_time),
            endTime: parseBackendDateTime(longestDwell.end_time) ?? new Date(longestDwell.end_time),
            placeName: longestDwell.place_name,
            placeType: longestDwell.place_type
          },
          coordinates: [longestDwell.latitude, longestDwell.longitude],
          placeName: longestDwell.place_name,
          placeType: longestDwell.place_type
        });
      });

      return dwellInsights.sort((a, b) => b.totalDwellTime - a.totalDwellTime);
    } catch (error) {
      console.error('Dwell analysis failed:', error);
      const msg = error instanceof Error ? error.message : String(error);
      setCircleErrors(prev => ({
        ...prev,
        [circle.id]: { ...(prev[circle.id] || {}), dwells: msg }
      }));
      return [];
    }
  }, [dateRange, queryTimeZone, selectedCourse, studentsOnly]);

  // Calculate distance using Haversine formula
  const calculateDistance = (lat1: number, lon1: number, lat2: number, lon2: number): number => {
    const R = 6371; // Earth's radius in km
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLon = (lon2 - lon1) * Math.PI / 180;
    const a = 
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
      Math.sin(dLon / 2) * Math.sin(dLon / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
  };

  // Detect co-location events
  const detectCoLocations = useCallback(async (circle: DrawCircle) => {
    if (!circle.dwellInsights || circle.dwellInsights.length < 2) return [];

    const coLocations: CoLocationInsight[] = [];
    const dwellInsights = circle.dwellInsights;

    // Compare all pairs of entities
    for (let i = 0; i < dwellInsights.length; i++) {
      for (let j = i + 1; j < dwellInsights.length; j++) {
        const entity1 = dwellInsights[i];
        const entity2 = dwellInsights[j];

        const dwells1 = entity1.dwells || [];
        const dwells2 = entity2.dwells || [];
        if (dwells1.length === 0 || dwells2.length === 0) continue;

        let best: {
          overlapSeconds: number;
          startMs: number;
          endMs: number;
          distanceKm: number;
          lat: number;
          lon: number;
          placeName?: string;
          placeType?: string;
        } | null = null;

        // Compare ALL dwell intervals to find any overlap (time + within 100m)
        for (const d1 of dwells1) {
          const s1 = d1.startTime.getTime();
          const e1 = d1.endTime.getTime();
          for (const d2 of dwells2) {
            const s2 = d2.startTime.getTime();
            const e2 = d2.endTime.getTime();

            const overlapStart = Math.max(s1, s2);
            const overlapEnd = Math.min(e1, e2);
            const overlapSeconds = (overlapEnd - overlapStart) / 1000;
            if (overlapSeconds <= 60) continue; // require >= 1 minute overlap

            const distanceKm = calculateDistance(d1.latitude, d1.longitude, d2.latitude, d2.longitude);
            if (distanceKm >= 0.1) continue; // require within 100m

            if (!best || overlapSeconds > best.overlapSeconds) {
              best = {
                overlapSeconds,
                startMs: overlapStart,
                endMs: overlapEnd,
                distanceKm,
                lat: (d1.latitude + d2.latitude) / 2,
                lon: (d1.longitude + d2.longitude) / 2,
                placeName: d1.placeName || d2.placeName,
                placeType: d1.placeType || d2.placeType
              };
            }
          }
        }

        if (best) {
          coLocations.push({
            entities: [entity1.entityId, entity2.entityId],
            deviceTypes: [entity1.deviceType, entity2.deviceType],
            overlapStart: new Date(best.startMs),
            overlapEnd: new Date(best.endMs),
            duration: best.overlapSeconds,
            location: [best.lat, best.lon],
            confidence: Math.min(1, 1 - (best.distanceKm / 0.1)),
            placeName: best.placeName,
            placeType: best.placeType
          });
        }
      }
    }

    return coLocations.sort((a, b) => b.duration - a.duration);
  }, []);

  // Format duration in seconds to human-readable string
  const formatDuration = (seconds: number): string => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = Math.floor(seconds % 60);
    
    if (hours > 0) {
      return minutes > 0 ? `${hours}h ${minutes}m` : `${hours}h`;
    } else if (minutes > 0) {
      return secs > 0 ? `${minutes}m ${secs}s` : `${minutes}m`;
    } else {
      return `${secs}s`;
    }
  };

  // Export Summary Tab to PDF
  const exportSummaryPDF = useCallback((circle: DrawCircle) => {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    
    doc.setFontSize(16);
    doc.setFont('helvetica', 'bold');
    doc.text(`Area Search: ${circle.name}`, pageWidth / 2, 15, { align: 'center' });
    
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.text(`Summary Report`, pageWidth / 2, 22, { align: 'center' });
    doc.text(`Generated: ${new Date().toLocaleString()}`, pageWidth / 2, 28, { align: 'center' });
    
    let y = 35;
    
    // Area details with geographic context
    doc.setFontSize(11);
    doc.setFont('helvetica', 'bold');
    doc.text('Search Area Details:', 14, y);
    y += 6;
    
    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    
    // Add location context if available
    if (circle.mapboxContext?.place_name) {
      doc.setFont('helvetica', 'bold');
      doc.text(`Location: ${circle.mapboxContext.place_name}`, 14, y);
      y += 5;
      doc.setFont('helvetica', 'normal');
      
      if (circle.mapboxContext.neighborhood || circle.mapboxContext.place) {
        const contextParts = [
          circle.mapboxContext.neighborhood, 
          circle.mapboxContext.place, 
          circle.mapboxContext.region
        ].filter(Boolean);
        doc.text(`${contextParts.join(', ')}`, 14, y);
        y += 5;
      }
    }
    
    doc.text(`Coordinates: ${circle.center[0].toFixed(6)}, ${circle.center[1].toFixed(6)}`, 14, y);
    y += 5;
    doc.text(`Radius: ${circle.radius}m`, 14, y);
    y += 8;
    
    // Statistics
    doc.setFontSize(11);
    doc.setFont('helvetica', 'bold');
    doc.text('Quick Statistics:', 14, y);
    y += 6;
    
    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    doc.text(`GPS Track Entities: ${circle.results?.length || 0}`, 14, y);
    y += 5;
    doc.text(`Dwell Insights: ${circle.dwellInsights?.length || 0}`, 14, y);
    y += 5;
    doc.text(`Co-Location Events: ${circle.coLocationInsights?.length || 0}`, 14, y);
    y += 10;
    
    // Top Dwells Preview
    if (circle.dwellInsights && circle.dwellInsights.length > 0) {
      doc.setFontSize(11);
      doc.setFont('helvetica', 'bold');
      doc.text('Top Dwell Activity:', 14, y);
      y += 6;
      
      circle.dwellInsights.slice(0, 5).forEach((insight) => {
        const hours = (insight.totalDwellTime / 3600).toFixed(1);
        doc.setFontSize(9);
        doc.setFont('helvetica', 'normal');
        doc.text(`• ${insight.entityId}: ${insight.dwellCount} dwells, ${hours}h total`, 18, y);
        y += 5;
      });
      y += 5;
    }
    
    // Top Co-Locations Preview
    if (circle.coLocationInsights && circle.coLocationInsights.length > 0) {
      doc.setFontSize(11);
      doc.setFont('helvetica', 'bold');
      doc.text('Top Co-Locations:', 14, y);
      y += 6;
      
      circle.coLocationInsights.slice(0, 5).forEach((coloc) => {
        const duration = formatDuration(coloc.duration);
        doc.setFontSize(9);
        doc.setFont('helvetica', 'normal');
        doc.text(`• ${coloc.entities.join(' & ')}: ${duration}`, 18, y);
        y += 5;
      });
    }
    
    doc.save(`${circle.name}-summary-${new Date().toISOString().split('T')[0]}.pdf`);
  }, []);

  // Export Dwells Tab to PDF
  const exportDwellsPDF = useCallback((circle: DrawCircle) => {
    if (!circle.dwellInsights || circle.dwellInsights.length === 0) return;
    
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    
    doc.setFontSize(16);
    doc.setFont('helvetica', 'bold');
    doc.text(`Dwell Activity: ${circle.name}`, pageWidth / 2, 15, { align: 'center' });
    
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.text(`Full Dwell Analysis Report`, pageWidth / 2, 22, { align: 'center' });
    doc.text(`Generated: ${new Date().toLocaleString()}`, pageWidth / 2, 28, { align: 'center' });
    
    const tableData = circle.dwellInsights.map((insight, idx) => {
      const totalTime = formatDuration(insight.totalDwellTime);
      const longestTime = formatDuration(insight.longestDwell.duration);
      const longestDate = insight.longestDwell.startTime.toLocaleString([], { 
        month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' 
      });
      
      return [
        idx + 1,
        insight.entityId,
        insight.deviceType,
        insight.dwellCount,
        totalTime,
        longestTime,
        longestDate,
        `${insight.coordinates[0].toFixed(5)}, ${insight.coordinates[1].toFixed(5)}`
      ];
    });
    
    autoTable(doc, {
      startY: 35,
      head: [['#', 'Entity', 'Device', 'Count', 'Total Time', 'Longest', 'Longest At', 'Location']],
      body: tableData,
      styles: { fontSize: 7, cellPadding: 1.5 },
      headStyles: { fillColor: [76, 175, 80], textColor: 255, fontStyle: 'bold' },
      alternateRowStyles: { fillColor: [245, 245, 245] },
      columnStyles: {
        0: { cellWidth: 10 },
        1: { cellWidth: 25 },
        2: { cellWidth: 18 },
        3: { cellWidth: 15 },
        4: { cellWidth: 20 },
        5: { cellWidth: 20 },
        6: { cellWidth: 30 },
        7: { cellWidth: 'auto' }
      }
    });
    
    doc.save(`${circle.name}-dwells-${new Date().toISOString().split('T')[0]}.pdf`);
  }, []);

  // Export Co-Locations Tab to PDF
  const exportCoLocationsPDF = useCallback((circle: DrawCircle) => {
    if (!circle.coLocationInsights || circle.coLocationInsights.length === 0) return;
    
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    
    doc.setFontSize(16);
    doc.setFont('helvetica', 'bold');
    doc.text(`Co-Location Events: ${circle.name}`, pageWidth / 2, 15, { align: 'center' });
    
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.text(`Full Co-Location Analysis Report`, pageWidth / 2, 22, { align: 'center' });
    doc.text(`Generated: ${new Date().toLocaleString()}`, pageWidth / 2, 28, { align: 'center' });
    
    const tableData = circle.coLocationInsights.map((coloc, idx) => {
      const duration = formatDuration(coloc.duration);
      const confidence = (coloc.confidence * 100).toFixed(0);
      const startTime = coloc.overlapStart.toLocaleString([], { 
        month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' 
      });
      const endTime = coloc.overlapEnd.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
      
      return [
        idx + 1,
        coloc.entities.join(' & '),
        coloc.deviceTypes.join(', '),
        duration,
        `${confidence}%`,
        startTime,
        endTime,
        `${coloc.location[0].toFixed(5)}, ${coloc.location[1].toFixed(5)}`
      ];
    });
    
    autoTable(doc, {
      startY: 35,
      head: [['#', 'Entities', 'Devices', 'Duration', 'Confidence', 'Start', 'End', 'Location']],
      body: tableData,
      styles: { fontSize: 7, cellPadding: 1.5 },
      headStyles: { fillColor: [255, 152, 0], textColor: 255, fontStyle: 'bold' },
      alternateRowStyles: { fillColor: [245, 245, 245] },
      columnStyles: {
        0: { cellWidth: 10 },
        1: { cellWidth: 35 },
        2: { cellWidth: 20 },
        3: { cellWidth: 18 },
        4: { cellWidth: 15 },
        5: { cellWidth: 30 },
        6: { cellWidth: 20 },
        7: { cellWidth: 'auto' }
      }
    });
    
    doc.save(`${circle.name}-colocations-${new Date().toISOString().split('T')[0]}.pdf`);
  }, []);

  // Export AI Insights Tab to PDF
  const exportAIInsightsPDF = useCallback((circle: DrawCircle) => {
    if (!circle.aiSummary) return;
    
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    const margin = 14;
    const maxWidth = pageWidth - (margin * 2);
    
    doc.setFontSize(16);
    doc.setFont('helvetica', 'bold');
    doc.text(`AI Analysis: ${circle.name}`, pageWidth / 2, 15, { align: 'center' });
    
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.text(`AI-Generated Insights Report`, pageWidth / 2, 22, { align: 'center' });
    doc.text(`Generated: ${new Date().toLocaleString()}`, pageWidth / 2, 28, { align: 'center' });
    
    let y = 40;
    
    // Area details
    doc.setFontSize(9);
    doc.setFont('helvetica', 'italic');
    doc.text(`Area: ${circle.center[0].toFixed(6)}, ${circle.center[1].toFixed(6)} • Radius: ${circle.radius}m`, margin, y);
    y += 10;
    
    // AI Summary content
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    const lines = doc.splitTextToSize(circle.aiSummary, maxWidth);
    lines.forEach((line: string) => {
      if (y > 280) { // Add new page if needed
        doc.addPage();
        y = 20;
      }
      doc.text(line, margin, y);
      y += 6;
    });
    
    doc.save(`${circle.name}-ai-insights-${new Date().toISOString().split('T')[0]}.pdf`);
  }, []);

  // Get geographic context via Geocoding API (Google or Mapbox)
  const getMapboxContext = useCallback(async (center: [number, number]) => {
    try {
      const GOOGLE_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || '';
      const MAPBOX_TOKEN = process.env.REACT_APP_MAPBOX_TOKEN || '';
      const useGoogle = (process.env.REACT_APP_MAP_PROVIDER || 'mapbox').toLowerCase() === 'google';
      const [lat, lon] = center;

      if (useGoogle && GOOGLE_KEY) {
        const resp = await fetch(
          `https://maps.googleapis.com/maps/api/geocode/json?latlng=${lat},${lon}&key=${GOOGLE_KEY}`
        );
        if (!resp.ok) return null;
        const data = await resp.json();
        const result = data.results?.[0];
        if (!result) return null;
        const context: any = { place_name: result.formatted_address, neighborhood: null, locality: null, place: null, region: null, country: null };
        for (const c of result.address_components || []) {
          if (c.types.includes('neighborhood')) context.neighborhood = c.long_name;
          if (c.types.includes('sublocality')) context.locality = c.long_name;
          if (c.types.includes('locality')) context.place = c.long_name;
          if (c.types.includes('administrative_area_level_1')) context.region = c.short_name;
          if (c.types.includes('country')) context.country = c.long_name;
        }
        return context;
      }

      // Reverse geocoding to get place context
      const response = await fetch(
        `https://api.mapbox.com/geocoding/v5/mapbox.places/${lon},${lat}.json?types=address,neighborhood,locality,place,region&access_token=${MAPBOX_TOKEN}`
      );
      
      if (!response.ok) {
        console.warn('Mapbox geocoding request failed');
        return null;
      }
      
      const data = await response.json();
      
      if (!data.features || data.features.length === 0) {
        return null;
      }
      
      // Extract context information
      const feature = data.features[0];
      const context: any = {
        place_name: feature.place_name,
        address: feature.text || null,
        neighborhood: null,
        locality: null,
        place: null,
        region: null,
        country: null
      };
      
      // Parse context from feature
      if (feature.context) {
        feature.context.forEach((ctx: any) => {
          if (ctx.id.startsWith('neighborhood')) {
            context.neighborhood = ctx.text;
          } else if (ctx.id.startsWith('locality')) {
            context.locality = ctx.text;
          } else if (ctx.id.startsWith('place')) {
            context.place = ctx.text;
          } else if (ctx.id.startsWith('region')) {
            context.region = ctx.text;
          } else if (ctx.id.startsWith('country')) {
            context.country = ctx.text;
          }
        });
      }
      
      return context;
    } catch (error) {
      console.error('Failed to get Mapbox context:', error);
      return null;
    }
  }, []);

  // Build a compact AdTech summary for the AI request from the area's raw
  // devices list (kept small so it fits the prompt token budget).
  const buildAdtechSummary = (adtechData?: AdtechAreaSearchResult | null): Record<string, any> | null => {
    if (!adtechData || !Array.isArray(adtechData.devices) || adtechData.devices.length === 0) return null;
    const carriers: Record<string, number> = {};
    const countries: Record<string, number> = {};
    // Count signals per registration id + tally carriers/countries.
    const perDevice = new Map<string, { signal_count: number; carrier?: string; country?: string }>();
    for (const d of adtechData.devices) {
      const rid = d.registration_id || 'unknown';
      const rec = perDevice.get(rid) || { signal_count: 0, carrier: d.carrier, country: d.country };
      rec.signal_count += 1;
      if (!rec.carrier && d.carrier) rec.carrier = d.carrier;
      if (!rec.country && d.country) rec.country = d.country;
      perDevice.set(rid, rec);
      if (d.carrier) carriers[d.carrier] = (carriers[d.carrier] || 0) + 1;
      if (d.country) countries[d.country] = (countries[d.country] || 0) + 1;
    }
    const top_devices = Array.from(perDevice.entries())
      .map(([registration_id, v]) => ({ registration_id, ...v }))
      .sort((a, b) => b.signal_count - a.signal_count)
      .slice(0, 8);
    return {
      unique_devices: adtechData.unique_count ?? perDevice.size,
      total_signals: adtechData.total_signals ?? adtechData.devices.length,
      carriers,
      countries,
      top_devices,
    };
  };

  // Get AI-powered summary
  const getAISummary = useCallback(async (circle: DrawCircle & { mapboxContext?: any; adtechData?: AdtechAreaSearchResult | null }) => {
    // Summarize when we have dwell/co-loc activity OR commercial-device presence.
    if (!circle.dwellInsights && !circle.coLocationInsights && !circle.adtechData) return null;

    try {
      // Use provided Mapbox context (already fetched in performFullAnalysis)
      const mapboxContext = circle.mapboxContext;
      
      // Format dwell insights with human-readable durations
      const formattedDwellInsights = circle.dwellInsights?.map(insight => ({
        ...insight,
        totalDwellTime_formatted: formatDuration(insight.totalDwellTime),
        totalDwellTime_seconds: insight.totalDwellTime,
        longestDwell: {
          ...insight.longestDwell,
          duration_formatted: formatDuration(insight.longestDwell.duration),
          duration_seconds: insight.longestDwell.duration
        }
      })) || [];

      // Format co-location insights with human-readable durations
      const formattedCoLocationInsights = circle.coLocationInsights?.map(coloc => ({
        ...coloc,
        duration_formatted: formatDuration(coloc.duration),
        duration_seconds: coloc.duration
      })) || [];

      // Use authenticated pathfinderApi method with geographic context
      const result = await pathfinderApi.generateAreaAnalysisAI({
        areaName: circle.name,
        center: circle.center,
        radius: circle.radius,
        entitiesCount: circle.results?.length || 0,
        dwellInsights: formattedDwellInsights,
        coLocationInsights: formattedCoLocationInsights,
        mapboxContext: mapboxContext, // Add geographic context
        useFullDetail: useFullDetail, // Toggle between smart summary and full detail
        adtechSummary: buildAdtechSummary(circle.adtechData), // Commercial-device presence
      });

      return result;
    } catch (error) {
      console.error('AI analysis failed:', error);
      return null;
    }
  }, [useFullDetail]);

  // Perform full analysis (search + dwells + co-location + AI)
  const performFullAnalysis = useCallback(async (circle: DrawCircle) => {
    // Set loading state
    onCircleUpdate({ ...circle, loading: true });
    
    try {
      // First, perform the area search to get GPS track points
      // We need to replicate the logic here to get the results directly
      if (!dateRange.start || !dateRange.end) {
        console.warn('[AreaSearch] Date range not selected - exiting early');
        return;
      }

      let searchStartDate: Date;
      let searchEndDate: Date;
      
      if (searchMode === 'current-time' && timeRange) {
        const bufferMinutes = 30;
        searchStartDate = new Date(timeRange.start.getTime() - (bufferMinutes * 60 * 1000));
        searchEndDate = new Date(timeRange.end.getTime() + (bufferMinutes * 60 * 1000));
      } else {
        searchStartDate = dateRange.start;
        searchEndDate = dateRange.end;
      }

      // Query GPS track points
      console.log('[AreaSearch] Querying area search:', {
        center: circle.center, radius: circle.radius,
        dates: [searchStartDate.toISOString(), searchEndDate.toISOString()],
        course: selectedCourse, studentsOnly
      });
      const rawResponse = await pathfinderApi.queryAreaSearch({
        center: circle.center,
        radius: circle.radius,
        dateRange: [searchStartDate, searchEndDate],
          timeZone: queryTimeZone,
        selectedCourse,
        excludeStudentIds: [],
        studentsOnly: studentsOnly
      });
      // Polygon areas query the backend with their bounding circle (the GPS
      // endpoint is radius-only), then clip precisely to the drawn ring so the
      // results match the actual polygon shape rather than the wider circle.
      const polyRing = circle.polygon;
      const isPoly = Array.isArray(polyRing) && polyRing.length >= 3;
      const response = isPoly
        ? rawResponse.filter((p) => {
            const c = p.coordinates; // [lon, lat]
            return Array.isArray(c) && pointInRing(c[0], c[1], polyRing!);
          })
        : rawResponse;
      console.log('[AreaSearch] Response:', response?.length, 'points', isPoly ? `(clipped from ${rawResponse?.length} to polygon)` : '');
      
      // Process results
      const results: AreaSearchResult[] = [];
      const entityGroups = new Map<string, PathfinderDataPoint[]>();
      
      response.forEach((point) => {
        const studentId = point.studentId || 'Unknown';
        const deviceType = point.deviceType || 'phone';
        const entityDeviceKey = `${studentId}-${deviceType}`;
        
        if (!entityGroups.has(entityDeviceKey)) {
          entityGroups.set(entityDeviceKey, []);
        }
        entityGroups.get(entityDeviceKey)!.push(point);
      });

      entityGroups.forEach((points, entityDeviceKey) => {
        if (points.length > 0) {
          const lastDashIndex = entityDeviceKey.lastIndexOf('-');
          const entityId = entityDeviceKey.substring(0, lastDashIndex);
          const deviceType = entityDeviceKey.substring(lastDashIndex + 1);
          
          // Calculate time range without spread operator to avoid stack overflow
          let minTs = Infinity, maxTs = -Infinity;
          points.forEach(p => {
            const ts = p.timestamp.getTime();
            if (ts < minTs) minTs = ts;
            if (ts > maxTs) maxTs = ts;
          });
          
          const entityType = entityId.startsWith('SRP') ? 'SRP' : 
                           entityId.startsWith('RP') ? 'RP' : 
                           entityId.startsWith('Student') ? 'Student' : 'Unknown';
          
          results.push({
            entityId: `${entityId} - ${deviceType}`,
            entityType,
            deviceType,
            pointCount: points.length,
            timeRange: {
              start: new Date(minTs),
              end: new Date(maxTs)
            },
            confidence: 0.95,
            dataPoints: points
          });
        }
      });

      // Run independent queries in parallel
      const [mapboxContext, dwellInsights, signalEnv, adtechData] = await Promise.all([
        getMapboxContext(circle.center),
        analyzeDwells(circle),
        pathfinderApi.getSignalEnvironmentArea({
          center: circle.center,
          radiusMeters: circle.radius,
          dateRange: [searchStartDate, searchEndDate],
          timeZone: queryTimeZone,
        }).catch((err) => {
          console.error('Signal environment area query failed:', err);
          return null;
        }),
        // AdTech (Venntel) area query — only when the module is available.
        // Polygon areas use the exact geofence (polygon) query; circles use the
        // radius query. Both return the same AdtechAreaSearchResult shape.
        adtechEnabled
          ? (isPoly
              ? pathfinderApi.queryAdtechGeofenceSearch({
                  polygon: polyRing!,
                  dateRange: [searchStartDate, searchEndDate],
                  bufferMeters: 0,
                  timeZone: queryTimeZone,
                })
              : pathfinderApi.queryAdtechAreaSearch({
                  center: circle.center,
                  radiusMeters: circle.radius,
                  dateRange: [searchStartDate, searchEndDate],
                  timeZone: queryTimeZone,
                })
            ).catch((err) => {
              console.error('AdTech area search failed:', err);
              return null;
            })
          : Promise.resolve(null),
      ]);

      // Detect co-locations (depends on dwellInsights)
      const coLocationInsights = await detectCoLocations({ ...circle, dwellInsights });
      
      // Get AI summary with geographic context + commercial-device presence
      const aiResult = await getAISummary({ ...circle, results, dwellInsights, coLocationInsights, mapboxContext, adtechData });
      
      // Final update with ALL data preserved
      const sortedResults = results.sort((a, b) => b.pointCount - a.pointCount);
      const finalCircle: DrawCircle = { 
        ...circle,
        results: sortedResults,
        dwellInsights,
        coLocationInsights,
        signalEnvironment: signalEnv,
        adtechData,
        aiSummary: aiResult?.summary || null,
        mapboxContext,
        loading: false
      };
      
      onCircleUpdate(finalCircle);

      // Auto-load all available results onto map and left device panel
      const allDataPoints = sortedResults.flatMap(r => r.dataPoints || []);
      const hasTracks = allDataPoints.length > 0;
      const hasDwells = dwellInsights && dwellInsights.length > 0;

      if (onLoadAreaData && hasTracks) {
        onLoadAreaData(allDataPoints, circle.id);
      }
      if (onLoadAreaDwells && hasDwells) {
        onLoadAreaDwells(dwellInsights, circle.id);
      }
    } catch (error) {
      console.error('Full analysis failed:', error);
      onCircleUpdate({ ...circle, loading: false });
    }
  }, [analyzeDwells, detectCoLocations, getAISummary, onCircleUpdate, onLoadAreaData, onLoadAreaDwells, dateRange, queryTimeZone, searchMode, timeRange, selectedCourse, studentsOnly, getMapboxContext]);

  // Auto-run the full analysis for freeform polygon areas as soon as they're
  // drawn (drawn circles wait for the operator to press "Analyze"). A ref guards
  // against re-triggering once results populate or while a run is in flight.
  const autoAnalyzedPolyIds = useRef<Set<string>>(new Set());
  useEffect(() => {
    if (!dateRange.start || !dateRange.end) return;
    drawCircles.forEach((c) => {
      const isPoly = Array.isArray(c.polygon) && c.polygon.length >= 3;
      if (!isPoly || c.loading || c.results || autoAnalyzedPolyIds.current.has(c.id)) return;
      autoAnalyzedPolyIds.current.add(c.id);
      performFullAnalysis(c);
    });
    // Drop ids for areas that no longer exist so re-drawn ids can run again.
    const live = new Set(drawCircles.map((c) => c.id));
    autoAnalyzedPolyIds.current.forEach((id) => { if (!live.has(id)) autoAnalyzedPolyIds.current.delete(id); });
  }, [drawCircles, dateRange.start, dateRange.end, performFullAnalysis]);

  const performSignalCompare = useCallback(async () => {
    if (drawCircles.length < 2 || !dateRange.start || !dateRange.end) return;

    setCompareLoading(true);
    setCompareResults(null);

    try {
      // Run cross-area comparison AND per-area full analysis in parallel
      const [result] = await Promise.all([
        pathfinderApi.compareSignalsAcrossAreas({
          areas: drawCircles.map(c => ({
            id: c.id,
            center: c.center,
            radiusMeters: c.radius,
          })),
          dateRange: [dateRange.start, dateRange.end],
          timeZone: queryTimeZone,
          mode: compareMode,
        }),
        // Also trigger full analysis for each area that hasn't been analyzed yet
        ...drawCircles
          .filter(c => !c.results || c.results.length === 0)
          .map(c => performFullAnalysis(c)),
      ]);
      setCompareResults(result);
    } catch (error) {
      console.error('Signal comparison failed:', error);
    } finally {
      setCompareLoading(false);
    }
  }, [drawCircles, dateRange, queryTimeZone, compareMode]);

  // Toggle circle visibility
  const toggleCircleVisibility = useCallback((circle: DrawCircle) => {
    const updatedCircle = { ...circle, visible: !circle.visible };
    onCircleUpdate(updatedCircle);
  }, [onCircleUpdate]);

  // const getEntityTypeDisplay = (entityType: string) => {
  //   const dotSx = { fontSize: 12 };
  //   switch (entityType) {
  //     case 'SRP':
  //       return { icon: <FiberManualRecord sx={{ ...dotSx, color: '#ff9800' }} />, color: '#ff9800', label: 'SRP' };
  //     case 'RP':
  //       return { icon: <FiberManualRecord sx={{ ...dotSx, color: '#4caf50' }} />, color: '#4caf50', label: 'RP' };
  //     case 'Student':
  //       return { icon: <FiberManualRecord sx={{ ...dotSx, color: '#2196f3' }} />, color: '#2196f3', label: 'Student' };
  //     default:
  //       return { icon: <HelpOutline sx={{ ...dotSx, color: '#757575' }} />, color: '#757575', label: 'Unknown' };
  //   }
  // };

  // Aggregate per-tile stats across all drawn circles so the top row tells the
  // analyst at a glance what their search has actually found, instead of just
  // a circle count. Mirrors Dwell Analysis' 5-tile pattern.
  const areaStats = (() => {
    let entities = 0;
    let dwells = 0;
    let coLocs = 0;
    let signals = 0;
    for (const c of drawCircles) {
      entities += c.results?.length ?? 0;
      dwells += c.dwellInsights?.length ?? 0;
      coLocs += c.coLocationInsights?.length ?? 0;
      const sig = c.signalEnvironment;
      if (sig) {
        signals += (sig.wifi?.length ?? 0) + (sig.bluetooth?.length ?? 0) + (sig.cellular?.length ?? 0);
      }
    }
    return { areas: drawCircles.length, entities, dwells, coLocs, signals };
  })();

  const filtersActive = studentsOnly || useFullDetail;

  return (
    <Box sx={{ p: 1.5, height: '100%', overflow: 'auto' }}>
      {/* Top-level sub-tabs: Search (draw + analyze) vs Saved Devices (watchlist). */}
      <Tabs
        value={mainTab}
        onChange={(_, v) => setMainTab(v)}
        variant="fullWidth"
        sx={{ minHeight: 36, mb: 1, '& .MuiTab-root': { minHeight: 36, textTransform: 'none', fontWeight: 600 } }}
      >
        <Tab icon={<SearchIcon sx={{ fontSize: 18 }} />} iconPosition="start" label="Search" />
        <Tab icon={<BookmarkIcon sx={{ fontSize: 18 }} />} iconPosition="start" label="Saved Devices" />
      </Tabs>

      {mainTab === 1 && (
        <SavedDevicesPanel
          courseId={selectedCourse}
          selectedStudents={_selectedStudents}
          students={students}
          adtechEnabled={adtechEnabled}
          onLoadStudents={(ids) => onLoadSavedDevices?.(ids)}
          onLoadAdtechPol={onLoadAdtechPol}
          adtechPolLoading={adtechPolLoading}
        />
      )}

      <Box sx={{ display: mainTab === 0 ? 'block' : 'none' }}>
      {/* Compact 5-tile stat row matches the Dwell Analysis layout: AREAS /
          ENTITIES / DWELLS / CO-LOCS / SIGNALS. Tiles light up only when their
          value > 0 so an empty search doesn't fight for attention. */}
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 0.5, mb: 1 }}>
        {[
          { label: 'Areas', value: areaStats.areas, color: '#42a5f5' },
          { label: 'Entities', value: areaStats.entities, color: '#66bb6a' },
          { label: 'Dwells', value: areaStats.dwells, color: '#ff9800' },
          { label: 'Co-locs', value: areaStats.coLocs, color: '#ab47bc' },
          { label: 'Signals', value: areaStats.signals, color: '#26a69a' },
        ].map(({ label, value, color }) => {
          const isActive = value > 0;
          return (
            <Box
              key={label}
              sx={{
                py: 0.5,
                borderRadius: 0.5,
                bgcolor: isActive ? `${color}14` : 'rgba(255,255,255,0.04)',
                border: '1px solid',
                borderColor: isActive ? `${color}66` : 'divider',
                textAlign: 'center',
              }}
            >
              <Typography
                variant="caption"
                sx={{
                  fontSize: '0.5rem',
                  textTransform: 'uppercase',
                  letterSpacing: 0.3,
                  display: 'block',
                  lineHeight: 1,
                  color: isActive ? color : 'text.secondary',
                  fontWeight: isActive ? 700 : 400,
                }}
              >
                {label}
              </Typography>
              <Typography variant="body2" fontWeight={700} sx={{ fontSize: '0.75rem', lineHeight: 1.3 }}>
                {value.toLocaleString()}
              </Typography>
            </Box>
          );
        })}
      </Box>

      {/* Primary drawing controls — Draw Circle button + Radius input. The
          previously-cluttered toggle pills moved into the Filters accordion
          below so this row stops overflowing on narrow side panels. */}
      <Box sx={{ 
        mb: 1, 
        p: 1, 
        bgcolor: 'background.paper', 
        borderRadius: 1, 
        border: '1px solid',
        borderColor: 'divider',
      }}>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
          <Button
            variant={isDrawingMode ? "contained" : "outlined"}
            onClick={() => onDrawModeChange(!isDrawingMode)}
            startIcon={<CircleIcon />}
            size="small"
            color={isDrawingMode ? "primary" : "inherit"}
            sx={{ minWidth: 110, height: 30, fontSize: '0.75rem' }}
          >
            {isDrawingMode ? 'Cancel' : 'Draw Circle'}
          </Button>

          <TextField
            label="Radius (m)"
            type="number"
            size="small"
            value={defaultRadius}
            onChange={(e) => onRadiusChange?.(Number(e.target.value))}
            sx={{ width: 100, '& .MuiInputBase-input': { fontSize: '0.75rem', py: 0.5 } }}
            inputProps={{ min: 10, max: 2500 }}
          />

          {/* Freeform polygon search area (Tracer-style): draw any shape and run
              the full area analysis inside it — entities, dwells, co-locations,
              signal environment, and (when enabled) AdTech — same as a circle. */}
          {onDrawAdtechPolygonModeChange && (
            <Button
              variant={isDrawingAdtechPolygon ? 'contained' : 'outlined'}
              onClick={() => onDrawAdtechPolygonModeChange(!isDrawingAdtechPolygon)}
              startIcon={adtechPolygonLoading ? <CircularProgress size={14} color="inherit" /> : <PentagonIcon />}
              size="small"
              disabled={adtechPolygonLoading}
              color={isDrawingAdtechPolygon ? 'primary' : 'inherit'}
              sx={{ minWidth: 130, height: 30, fontSize: '0.75rem' }}
            >
              {adtechPolygonLoading ? 'Searching…' : isDrawingAdtechPolygon ? 'Cancel Polygon' : 'Draw Polygon'}
            </Button>
          )}
        </Box>

        {isDrawingMode && (
          <Typography variant="caption" sx={{ 
            color: 'info.main', 
            fontStyle: 'italic',
            display: 'flex',
            alignItems: 'center',
            mt: 0.75
          }}>
            <Lightbulb sx={{ fontSize: 14, mr: 0.5 }} /> Click on map to place circle
          </Typography>
        )}

        {isDrawingAdtechPolygon && (
          <Typography variant="caption" sx={{ color: 'info.main', fontStyle: 'italic', display: 'flex', alignItems: 'center', mt: 0.75 }}>
            <Lightbulb sx={{ fontSize: 14, mr: 0.5 }} /> Click the map to add polygon vertices; double-click to finish and analyze everything inside it.
          </Typography>
        )}
      </Box>

      {/* Direct ID / IP lookup (AdTech): paste registration ids and/or IPv4
          addresses to pull their full location history for the selected range
          and plot them as device tracks — no drawing required. */}
      {adtechEnabled && (onLoadAdtechPol || onLookupAdtechIps) && (
        <Box sx={{ mb: 1, p: 1, bgcolor: 'background.paper', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
          <Typography variant="caption" fontWeight={700} sx={{ fontSize: '0.7rem', display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
            <MyLocationIcon sx={{ fontSize: 14 }} /> ID / IP Lookup
          </Typography>
          <Box sx={{ display: 'flex', gap: 0.75, alignItems: 'flex-start' }}>
            <TextField
              size="small"
              fullWidth
              multiline
              maxRows={3}
              placeholder="Registration IDs or IPs (comma / space / newline separated)"
              value={lookupText}
              onChange={(e) => setLookupText(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) { e.preventDefault(); handleIdIpLookup(); }
              }}
              sx={{ '& .MuiInputBase-input': { fontSize: '0.72rem' } }}
            />
            <Button
              variant="contained"
              size="small"
              disabled={adtechPolLoading || !lookupText.trim()}
              onClick={handleIdIpLookup}
              startIcon={adtechPolLoading ? <CircularProgress size={12} color="inherit" /> : <SearchIcon sx={{ fontSize: 14 }} />}
              sx={{ minWidth: 84, height: 30, fontSize: '0.72rem', textTransform: 'none', whiteSpace: 'nowrap' }}
            >
              Lookup
            </Button>
          </Box>
          <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.6rem', display: 'block', mt: 0.5 }}>
            IPv4 addresses are resolved to devices; everything else is treated as a registration ID. Ctrl/Cmd+Enter to run.
          </Typography>
        </Box>
      )}

      {/* Filters accordion — collapsed by default with "Active" chip when any
          toggle is non-default. Houses the previously-overflowing entity-type
          and AI-detail pills. Same UX as Dwell Analysis / Signal Environment. */}
      <Accordion
        defaultExpanded={false}
        sx={{
          mb: 1,
          '&::before': { display: 'none' },
          boxShadow: 'none',
          bgcolor: 'transparent',
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: '4px !important',
        }}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon sx={{ fontSize: 16 }} />}
          sx={{ minHeight: 28, '&.Mui-expanded': { minHeight: 28 }, py: 0, '& .MuiAccordionSummary-content': { my: 0.25 } }}
        >
          <Typography variant="caption" fontWeight="bold" sx={{ fontSize: '0.7rem' }}>
            Filters
          </Typography>
          {filtersActive && (
            <Chip
              label="Active"
              size="small"
              color="primary"
              sx={{ ml: 1, height: 14, fontSize: '0.5rem', '& .MuiChip-label': { px: 0.4 } }}
            />
          )}
        </AccordionSummary>
        <AccordionDetails sx={{ pt: 0, pb: 0.75, px: 1.5 }}>
          <Stack spacing={1}>
            <Box>
              <Typography variant="caption" sx={{ fontSize: '0.6rem', color: 'text.secondary', display: 'block', mb: 0.25 }}>
                Entity scope
              </Typography>
              <ToggleButtonGroup
                value={studentsOnly ? 'students' : 'all'}
                exclusive
                onChange={(_, val) => val !== null && setStudentsOnly(val === 'students')}
                size="small"
                fullWidth
                sx={{ height: 26 }}
              >
                <ToggleButton value="all" sx={{ py: 0, fontSize: '0.7rem' }}>
                  <Tooltip title="Show all entities">
                    <span>All</span>
                  </Tooltip>
                </ToggleButton>
                <ToggleButton value="students" sx={{ py: 0, fontSize: '0.7rem' }}>
                  <Tooltip title="Filter to Operators only">
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <School sx={{ fontSize: 14 }} />
                      <span>Operators</span>
                    </Box>
                  </Tooltip>
                </ToggleButton>
              </ToggleButtonGroup>
            </Box>
            <Box>
              <Typography variant="caption" sx={{ fontSize: '0.6rem', color: 'text.secondary', display: 'block', mb: 0.25 }}>
                AI detail level
              </Typography>
              <ToggleButtonGroup
                value={useFullDetail ? 'full' : 'summary'}
                exclusive
                onChange={(_, val) => val !== null && setUseFullDetail(val === 'full')}
                size="small"
                fullWidth
                sx={{ height: 26 }}
              >
                <ToggleButton value="summary" sx={{ py: 0, fontSize: '0.7rem' }}>
                  <Tooltip title="AI receives smart summary (recommended)">
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Lightbulb sx={{ fontSize: 14 }} />
                      <span>Summary</span>
                    </Box>
                  </Tooltip>
                </ToggleButton>
                <ToggleButton value="full" sx={{ py: 0, fontSize: '0.7rem' }}>
                  <Tooltip title="AI receives ALL data (may hit token limits)">
                    <span>Full Detail</span>
                  </Tooltip>
                </ToggleButton>
              </ToggleButtonGroup>
            </Box>
          </Stack>
        </AccordionDetails>
      </Accordion>

      {/* Cross-Area Signal Comparison */}
      {drawCircles.length >= 2 && (
        <Box sx={{
          mb: 1.5,
          p: 1.5,
          bgcolor: 'background.paper',
          borderRadius: 1,
          border: '1px solid',
          borderColor: compareResults ? 'secondary.main' : 'divider',
        }}>
          <Typography variant="subtitle2" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
            <CompareIcon sx={{ fontSize: 16 }} /> Cross-Area Signal Comparison
          </Typography>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <ToggleButtonGroup
              value={compareMode}
              exclusive
              onChange={(_, val) => val !== null && setCompareMode(val)}
              size="small"
              sx={{ height: 32 }}
            >
              <ToggleButton value="intersection" sx={{ px: 1.5, py: 0, fontSize: '0.75rem' }}>
                <Tooltip title="Signals seen in ALL areas">
                  <span>Common</span>
                </Tooltip>
              </ToggleButton>
              <ToggleButton value="union" sx={{ px: 1.5, py: 0, fontSize: '0.75rem' }}>
                <Tooltip title="Signals seen in 2+ areas">
                  <span>Shared (2+)</span>
                </Tooltip>
              </ToggleButton>
            </ToggleButtonGroup>

            <Button
              variant="contained"
              color="secondary"
              size="small"
              startIcon={compareLoading ? <CircularProgress size={16} /> : <CompareIcon />}
              onClick={performSignalCompare}
              disabled={compareLoading || !dateRange.start || !dateRange.end}
              sx={{ minWidth: 140 }}
            >
              {compareLoading ? 'Comparing...' : compareMode === 'intersection' ? 'Find Common' : 'Find Shared'}
            </Button>

            {compareResults && (
              <Button
                size="small"
                variant="text"
                startIcon={<ClearIcon />}
                onClick={() => setCompareResults(null)}
                sx={{ fontSize: '0.7rem' }}
              >
                Clear
              </Button>
            )}
          </Box>

          <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
            {compareMode === 'intersection'
              ? `Find WiFi/BLE/Cell signals observed in all ${drawCircles.length} areas`
              : `Find WiFi/BLE/Cell signals observed in 2 or more areas`}
          </Typography>
        </Box>
      )}

      {/* Cross-Area Comparison Results */}
      {compareResults && (
        <Card sx={{ mb: 1.5, border: '1px solid', borderColor: 'secondary.main' }}>
          <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
            <Typography variant="subtitle2" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
              <CompareIcon sx={{ fontSize: 16, color: 'secondary.main' }} />
              {compareResults.mode === 'intersection' ? 'Common' : 'Shared'} Signals Across {compareResults.area_count} Areas
            </Typography>

            {/* Summary chips */}
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mb: 1.5 }}>
              {compareResults.wifi.length > 0 && (
                <Chip icon={<WifiIcon />} label={`${compareResults.wifi.length} WiFi`} size="small" color="primary" />
              )}
              {compareResults.bluetooth.length > 0 && (
                <Chip icon={<BluetoothIcon />} label={`${compareResults.bluetooth.length} BLE`} size="small" color="info" />
              )}
              {compareResults.cellular.length > 0 && (
                <Chip icon={<CellTowerIcon />} label={`${compareResults.cellular.length} Cell`} size="small" color="warning" />
              )}
              {compareResults.wifi.length === 0 && compareResults.bluetooth.length === 0 && compareResults.cellular.length === 0 && (
                <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                  No {compareResults.mode === 'intersection' ? 'common' : 'shared'} signals found across the selected areas.
                </Typography>
              )}
            </Box>

            <Box sx={{ maxHeight: 450, overflow: 'auto' }}>
              {/* Common WiFi */}
              {compareResults.wifi.length > 0 && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="caption" sx={{ fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5, color: 'primary.main' }}>
                    <WifiIcon sx={{ fontSize: 14 }} /> WiFi APs ({compareResults.wifi.length})
                  </Typography>
                  {compareResults.wifi.map((w, idx) => {
                    const areaNames = (w.area_ids || []).map(aid => {
                      const cIdx = drawCircles.findIndex(c => c.id === aid);
                      return cIdx >= 0 ? drawCircles[cIdx].name : aid;
                    });
                    return (
                      <Box key={idx} sx={{ mb: 0.75, p: 0.75, bgcolor: 'action.hover', borderRadius: 0.5 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                          <Typography variant="caption" sx={{ fontSize: '0.75rem', fontWeight: 'bold', fontFamily: 'monospace' }}>
                            {w.bssid}
                            {w.ssid && <span style={{ fontFamily: 'inherit', fontWeight: 'normal' }}> — {w.ssid}</span>}
                          </Typography>
                          <Chip
                            label={`${w.areas_found}/${compareResults.area_count} areas`}
                            size="small"
                            color={w.areas_found === compareResults.area_count ? 'success' : 'default'}
                            sx={{ height: 18, fontSize: '0.65rem', ml: 0.5, flexShrink: 0 }}
                          />
                        </Box>
                        <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'text.secondary', display: 'block' }}>
                          {w.manufacturer || 'Unknown mfr'} • {w.observation_count} obs
                          {w.avg_signal_dbm != null ? ` • ${w.avg_signal_dbm} dBm` : ''}
                          {w.frequency_mhz ? ` • ${w.frequency_mhz} MHz` : ''}
                        </Typography>
                        <Typography variant="caption" sx={{ fontSize: '0.65rem', color: 'text.disabled', display: 'block' }}>
                          Found in: {areaNames.join(', ')}
                        </Typography>
                      </Box>
                    );
                  })}
                </Box>
              )}

              {/* Common BLE */}
              {compareResults.bluetooth.length > 0 && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="caption" sx={{ fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5, color: 'info.main' }}>
                    <BluetoothIcon sx={{ fontSize: 14 }} /> BLE Devices ({compareResults.bluetooth.length})
                  </Typography>
                  {compareResults.bluetooth.map((b, idx) => {
                    const areaNames = (b.area_ids || []).map(aid => {
                      const cIdx = drawCircles.findIndex(c => c.id === aid);
                      return cIdx >= 0 ? drawCircles[cIdx].name : aid;
                    });
                    return (
                      <Box key={idx} sx={{ mb: 0.75, p: 0.75, bgcolor: 'action.hover', borderRadius: 0.5 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                          <Typography variant="caption" sx={{ fontSize: '0.75rem', fontWeight: 'bold' }}>
                            {b.name || b.address}
                            {b.name && b.name !== b.address && (
                              <span style={{ fontWeight: 'normal', fontFamily: 'monospace', marginLeft: 4, fontSize: '0.7rem' }}>({b.address})</span>
                            )}
                          </Typography>
                          <Chip
                            label={`${b.areas_found}/${compareResults.area_count} areas`}
                            size="small"
                            color={b.areas_found === compareResults.area_count ? 'success' : 'default'}
                            sx={{ height: 18, fontSize: '0.65rem', ml: 0.5, flexShrink: 0 }}
                          />
                        </Box>
                        <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'text.secondary', display: 'block' }}>
                          {b.manufacturer || 'Unknown mfr'} • {b.observation_count} obs
                          {b.avg_rssi != null ? ` • ${b.avg_rssi} dBm` : ''}
                          {b.bt_type ? ` • ${b.bt_type}` : ''}
                        </Typography>
                        <Typography variant="caption" sx={{ fontSize: '0.65rem', color: 'text.disabled', display: 'block' }}>
                          Found in: {areaNames.join(', ')}
                        </Typography>
                      </Box>
                    );
                  })}
                </Box>
              )}

              {/* Common Cellular */}
              {compareResults.cellular.length > 0 && (
                <Box sx={{ mb: 1 }}>
                  <Typography variant="caption" sx={{ fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5, color: 'warning.main' }}>
                    <CellTowerIcon sx={{ fontSize: 14 }} /> Cell Towers ({compareResults.cellular.length})
                  </Typography>
                  {compareResults.cellular.map((c, idx) => {
                    const areaNames = (c.area_ids || []).map(aid => {
                      const cIdx = drawCircles.findIndex(ci => ci.id === aid);
                      return cIdx >= 0 ? drawCircles[cIdx].name : aid;
                    });
                    return (
                      <Box key={idx} sx={{ mb: 0.75, p: 0.75, bgcolor: 'action.hover', borderRadius: 0.5 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                          <Typography variant="caption" sx={{ fontSize: '0.75rem', fontWeight: 'bold', fontFamily: 'monospace' }}>
                            {c.cell_tower_id}
                            {c.operator && <span style={{ fontFamily: 'inherit', fontWeight: 'normal' }}> — {c.operator}</span>}
                          </Typography>
                          <Chip
                            label={`${c.areas_found}/${compareResults.area_count} areas`}
                            size="small"
                            color={c.areas_found === compareResults.area_count ? 'success' : 'default'}
                            sx={{ height: 18, fontSize: '0.65rem', ml: 0.5, flexShrink: 0 }}
                          />
                        </Box>
                        <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'text.secondary', display: 'block' }}>
                          {c.network_type || '—'} • {c.observation_count} obs
                          {c.avg_signal_dbm != null ? ` • ${c.avg_signal_dbm} dBm` : ''}
                          {c.mcc && c.mnc ? ` • MCC/MNC ${c.mcc}/${c.mnc}` : ''}
                        </Typography>
                        <Typography variant="caption" sx={{ fontSize: '0.65rem', color: 'text.disabled', display: 'block' }}>
                          Found in: {areaNames.join(', ')}
                        </Typography>
                      </Box>
                    );
                  })}
                </Box>
              )}
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Drawn Circles List */}
      <Card>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
            <Typography variant="subtitle2">
              Search Areas ({drawCircles.length})
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Tooltip title="Show saved areas">
                <span>
                  <Button
                    size="small"
                    startIcon={<BookmarkIcon sx={{ fontSize: 16 }} />}
                    onClick={() => setSavedAreasOpen((v) => !v)}
                    color={savedAreasOpen ? 'primary' : 'inherit'}
                  >
                    Saved ({savedAreas.length})
                  </Button>
                </span>
              </Tooltip>
              {drawCircles.length > 0 && (
                <Button
                  size="small"
                  startIcon={<ClearIcon />}
                  onClick={() => drawCircles.forEach(c => onCircleDelete(c.id))}
                >
                  Clear All
                </Button>
              )}
            </Box>
          </Box>

          {/* Saved Areas (persisted geofences) — reload as editable circles */}
          {savedAreasOpen && (
            <Box sx={{ mb: 1.5, p: 1, bgcolor: 'action.hover', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                <BookmarkIcon sx={{ fontSize: 14, color: 'primary.main' }} />
                <Typography variant="caption" sx={{ fontWeight: 'bold' }}>Saved Areas</Typography>
                {savedAreasLoading && <CircularProgress size={12} sx={{ ml: 0.5 }} />}
              </Box>
              {savedAreas.length === 0 && !savedAreasLoading ? (
                <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                  No saved areas yet. Draw a circle, then use the bookmark to save it.
                </Typography>
              ) : (
                savedAreas.map((area) => (
                  <Box key={area.id} sx={{ display: 'flex', alignItems: 'center', gap: 0.5, py: 0.25 }}>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="caption" noWrap sx={{ display: 'block', fontWeight: 500 }}>
                        {area.name}
                      </Typography>
                    </Box>
                    <Tooltip title="Load this area onto the map">
                      <IconButton size="small" onClick={() => loadSavedArea(area)}>
                        <MyLocationIcon sx={{ fontSize: 14, color: 'primary.main' }} />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete saved area">
                      <IconButton size="small" onClick={() => deleteSavedArea(area.id)}>
                        <DeleteIcon sx={{ fontSize: 14 }} />
                      </IconButton>
                    </Tooltip>
                    {intelEnabled && (
                      <Tooltip title="Promote to Intel Packet">
                        <IconButton
                          size="small"
                          onClick={() => {
                            setPromoteExtras([]);
                            setPromoteName(`Area: ${area.name}`);
                            setPromoteTarget({
                              member_type: 'geofence',
                              member_id: String(area.id),
                              label: area.name,
                              is_primary: true,
                            });
                          }}
                        >
                          <FactCheckIcon sx={{ fontSize: 14, color: '#ffb300' }} />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Box>
                ))
              )}
            </Box>
          )}

          {drawCircles.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
              No search areas drawn. Click "Draw Circle" to create one.
            </Typography>
          ) : (
            <List dense>
              {drawCircles.map((circle) => (
                <ListItem key={circle.id} sx={{ px: 0 }}>
                  <Box sx={{ width: '100%' }}>
                    {/* Circle Info */}
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                      <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                        {circle.name}
                      </Typography>
                      <Box>
                        <Tooltip title="Save this area">
                          <IconButton size="small" onClick={() => beginSaveArea(circle)}>
                            <BookmarkAddIcon />
                          </IconButton>
                        </Tooltip>
                        {intelEnabled && (
                          <Tooltip title="Promote area + selected devices to Intel Packet">
                            <IconButton
                              size="small"
                              onClick={() => {
                                const extras: PromoteTarget[] = (circle.results || []).slice(0, 40).map((r) => ({
                                  member_type: 'device' as const,
                                  member_id: r.entityId,
                                  label: r.entityId,
                                }));
                                (circle.adtechData?.devices || []).slice(0, 40).forEach((d: any) => {
                                  const rid = String(d.registration_id || d.registrationID || '');
                                  if (rid) extras.push({ member_type: 'adtech_id', member_id: rid, label: rid });
                                });
                                setPromoteExtras(extras);
                                setPromoteName(`Area search: ${circle.name}`);
                                setPromoteTarget({
                                  member_type: 'area',
                                  member_id: circle.id,
                                  label: circle.name,
                                  is_primary: true,
                                  meta: { center: circle.center, radius: circle.radius },
                                });
                              }}
                            >
                              <FactCheckIcon sx={{ color: '#ffb300' }} />
                            </IconButton>
                          </Tooltip>
                        )}
                        <Tooltip title={circle.visible ? "Hide on map" : "Show on map"}>
                          <IconButton size="small" onClick={() => toggleCircleVisibility(circle)}>
                            {circle.visible ? <VisibilityIcon /> : <VisibilityOffIcon />}
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete circle">
                          <IconButton size="small" onClick={() => onCircleDelete(circle.id)}>
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </Box>

                    {/* Inline "name & save" field for persisting this area */}
                    {savingAreaId === circle.id && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
                        <TextField
                          size="small"
                          autoFocus
                          fullWidth
                          placeholder="Area name"
                          value={areaNameDraft}
                          onChange={(e) => setAreaNameDraft(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') commitSaveArea(circle);
                            if (e.key === 'Escape') { setSavingAreaId(null); setAreaNameDraft(''); }
                          }}
                          sx={{ '& .MuiInputBase-input': { fontSize: '0.75rem', py: 0.5 } }}
                        />
                        <Tooltip title="Save area">
                          <IconButton size="small" onClick={() => commitSaveArea(circle)} disabled={!areaNameDraft.trim()}>
                            <CheckIcon sx={{ fontSize: 18, color: 'success.main' }} />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Cancel">
                          <IconButton size="small" onClick={() => { setSavingAreaId(null); setAreaNameDraft(''); }}>
                            <CloseIcon sx={{ fontSize: 18 }} />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    )}

                    {/* Circle Details with Geographic Context */}
                    {circle.mapboxContext?.place_name ? (
                      <Box>
                        <Typography variant="caption" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, fontWeight: 'bold', color: 'primary.main' }}>
                          <LocationOn sx={{ fontSize: 14 }} /> {circle.mapboxContext.place_name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontSize: '0.65rem' }}>
                          {circle.center[0].toFixed(6)}, {circle.center[1].toFixed(6)} • Radius: {circle.radius}m
                        </Typography>
                      </Box>
                    ) : (
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                        Center: {circle.center[0].toFixed(6)}, {circle.center[1].toFixed(6)} | 
                        Radius: {circle.radius}m
                      </Typography>
                    )}

                    {/* Analyze Button + optional AdTech count preview */}
                    <Box sx={{ mt: 1, mb: 1, display: 'flex', gap: 0.75, alignItems: 'center', flexWrap: 'wrap' }}>
                      <Button
                        size="small"
                        variant="contained"
                        startIcon={circle.loading ? <CircularProgress size={16} /> : <SearchIcon />}
                        onClick={() => performFullAnalysis(circle)}
                        disabled={circle.loading || !dateRange.start || !dateRange.end}
                      >
                        {circle.loading ? 'Analyzing...' : 'Analyze Area'}
                      </Button>
                      {adtechEnabled && (
                        <Tooltip title="Preview how many AdTech devices/signals this area holds before a full pull">
                          <span>
                            <Button
                              size="small"
                              variant="outlined"
                              color="secondary"
                              startIcon={areaPreview[circle.id]?.loading ? <CircularProgress size={14} color="inherit" /> : <MyLocationIcon sx={{ fontSize: 16 }} />}
                              onClick={() => handlePreviewArea(circle)}
                              disabled={areaPreview[circle.id]?.loading || !dateRange.start || !dateRange.end}
                              sx={{ textTransform: 'none' }}
                            >
                              Preview count
                            </Button>
                          </span>
                        </Tooltip>
                      )}
                    </Box>
                    {adtechEnabled && areaPreview[circle.id] && !areaPreview[circle.id]?.loading && (
                      <Box sx={{ mb: 1 }}>
                        {areaPreview[circle.id]?.error ? (
                          <Typography variant="caption" sx={{ color: 'error.main', fontSize: '0.65rem' }}>
                            {areaPreview[circle.id]?.error}
                          </Typography>
                        ) : (
                          <Chip
                            size="small"
                            color="secondary"
                            variant="outlined"
                            label={`~${(areaPreview[circle.id]?.devices ?? 0).toLocaleString()} devices · ${(areaPreview[circle.id]?.signals ?? 0).toLocaleString()} signals${areaPreview[circle.id]?.estimated ? ' (est.)' : ''}`}
                            sx={{ height: 20, fontSize: '0.62rem' }}
                          />
                        )}
                      </Box>
                    )}

                    {/* Analysis Results with Sub-Tabs */}
                    {(circle.dwellInsights || circle.coLocationInsights || circle.results || circle.signalEnvironment || circle.adtechData) && !circle.loading && (
                      <Box sx={{ mt: 2, bgcolor: 'background.default', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
                        {/* Sub-Tabs */}
                        <Tabs
                          value={circleSubTabs[circle.id] || 0}
                          onChange={(_e, newValue) => setCircleSubTabs(prev => ({ ...prev, [circle.id]: newValue }))}
                          variant="scrollable"
                          scrollButtons="auto"
                          sx={{ borderBottom: 1, borderColor: 'divider', minHeight: 36 }}
                        >
                          <Tab label={<Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}><BarChart sx={{ fontSize: 14 }} /> Summary</Box>} sx={{ minHeight: 36, py: 0.5, fontSize: '0.75rem' }} />
                          <Tab 
                            label={<Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}><Home sx={{ fontSize: 14 }} /> Dwells ({circle.dwellInsights?.length || 0})</Box>} 
                            sx={{ minHeight: 36, py: 0.5, fontSize: '0.75rem' }}
                            disabled={!circle.dwellInsights || circle.dwellInsights.length === 0}
                          />
                          <Tab 
                            label={<Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}><Groups sx={{ fontSize: 14 }} /> Co-Locations ({circle.coLocationInsights?.length || 0})</Box>} 
                            sx={{ minHeight: 36, py: 0.5, fontSize: '0.75rem' }}
                            disabled={!circle.coLocationInsights || circle.coLocationInsights.length === 0}
                          />
                          <Tab 
                            label={
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <SignalsIcon sx={{ fontSize: 14 }} /> Signals
                                {circle.signalEnvironment && (
                                  <span>({(circle.signalEnvironment.wifi?.length || 0) + (circle.signalEnvironment.bluetooth?.length || 0) + (circle.signalEnvironment.cellular?.length || 0)})</span>
                                )}
                              </Box>
                            } 
                            sx={{ minHeight: 36, py: 0.5, fontSize: '0.75rem' }}
                            disabled={!circle.signalEnvironment || ((circle.signalEnvironment.wifi?.length || 0) + (circle.signalEnvironment.bluetooth?.length || 0) + (circle.signalEnvironment.cellular?.length || 0)) === 0}
                          />
                          <Tab 
                            label={<Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}><LaptopMac sx={{ fontSize: 14 }} /> AI Insights</Box>} 
                            sx={{ minHeight: 36, py: 0.5, fontSize: '0.75rem' }}
                            disabled={!circle.aiSummary}
                          />
                          {adtechEnabled && (
                            <Tab
                              label={
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                  <SignalsIcon sx={{ fontSize: 14, color: '#e91e63' }} /> AdTech
                                  {circle.adtechData && <span>({circle.adtechData.unique_count})</span>}
                                </Box>
                              }
                              sx={{ minHeight: 36, py: 0.5, fontSize: '0.75rem' }}
                              disabled={!circle.adtechData || circle.adtechData.unique_count === 0}
                            />
                          )}
                        </Tabs>

                        {/* Tab Panels */}
                        <Box sx={{ p: 2 }}>
                          {/* Summary Tab */}
                          {(circleSubTabs[circle.id] || 0) === 0 && (
                            <Box>
                              {circleErrors[circle.id]?.dwells && (
                                <Box sx={{ mb: 2 }}>
                                  <Typography variant="caption" color="error" sx={{ display: 'block', fontWeight: 'bold' }}>
                                    Dwell query failed
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                    {circleErrors[circle.id]?.dwells}
                                  </Typography>
                                </Box>
                              )}
                              {/* Export Button */}
                              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
                                <Button
                                  size="small"
                                  variant="outlined"
                                  startIcon={<PdfIcon />}
                                  onClick={() => exportSummaryPDF(circle)}
                                  sx={{ fontSize: '0.7rem', py: 0.5 }}
                                >
                                  Export
                                </Button>
                              </Box>
                              
                              {/* Geographic Context */}
                              {circle.mapboxContext?.place_name && (
                                <Box sx={{ mb: 2, p: 1.5, bgcolor: 'action.hover', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
                                  <Typography variant="caption" sx={{ fontWeight: 'bold', color: 'primary.main', display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                                    <LocationOn sx={{ fontSize: 14 }} /> Location Context
                                  </Typography>
                                  <Typography variant="caption" sx={{ fontSize: '0.75rem', display: 'block' }}>
                                    {circle.mapboxContext.place_name}
                                  </Typography>
                                  {(circle.mapboxContext.neighborhood || circle.mapboxContext.place) && (
                                    <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'text.secondary', display: 'block', mt: 0.5 }}>
                                      {[circle.mapboxContext.neighborhood, circle.mapboxContext.place, circle.mapboxContext.region].filter(Boolean).join(', ')}
                                    </Typography>
                                  )}
                                </Box>
                              )}
                              
                              {/* Entities Found */}
                              {circle.results && circle.results.length > 0 ? (
                                <Box sx={{ mb: 2, display: 'flex', flexDirection: 'column', gap: 1 }}>
                                  <Typography variant="caption" sx={{ fontWeight: 'bold', color: 'primary.main', display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    <LocationOn sx={{ fontSize: 14 }} /> {circle.results.length} Entities with GPS Track Data
                          </Typography>
                        </Box>
                              ) : circle.dwellInsights && circle.dwellInsights.length > 0 && (
                                <Box sx={{ mb: 2 }}>
                                  <Typography variant="caption" sx={{ fontStyle: 'italic', color: 'text.secondary' }}>
                                    No GPS track points found in this area (only dwells detected)
                                  </Typography>
                                </Box>
                              )}

                              {/* Quick Stats */}
                              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                                {circle.dwellInsights && circle.dwellInsights.length > 0 && (
                              <Chip
                                    icon={<Home sx={{ fontSize: 14 }} />}
                                    label={`${circle.dwellInsights.length} Dwells`} 
                                size="small"
                                    color="primary" 
                                    variant="outlined"
                                  />
                                )}
                                {circle.coLocationInsights && circle.coLocationInsights.length > 0 && (
                                  <Chip 
                                    icon={<Groups sx={{ fontSize: 14 }} />}
                                    label={`${circle.coLocationInsights.length} Co-Locations`} 
                                    size="small" 
                                    color="warning" 
                                    variant="outlined"
                                  />
                                )}
                                {circle.signalEnvironment && ((circle.signalEnvironment.wifi?.length || 0) + (circle.signalEnvironment.bluetooth?.length || 0) + (circle.signalEnvironment.cellular?.length || 0)) > 0 && (
                                  <Chip 
                                    icon={<SignalsIcon sx={{ fontSize: 14 }} />}
                                    label={`${(circle.signalEnvironment.wifi?.length || 0) + (circle.signalEnvironment.bluetooth?.length || 0) + (circle.signalEnvironment.cellular?.length || 0)} Signals`} 
                                    size="small" 
                                    color="secondary" 
                                    variant="outlined"
                                  />
                                )}
                                {circle.aiSummary && (
                                  <Chip 
                                    icon={<LaptopMac sx={{ fontSize: 14 }} />}
                                    label="AI Analysis Available" 
                                    size="small" 
                                    color="info" 
                                    variant="outlined"
                                  />
                                )}
                            </Box>

                              {/* Preview snippets */}
                              {circle.dwellInsights && circle.dwellInsights.length > 0 && (
                                <Box sx={{ mb: 1 }}>
                                  <Typography variant="caption" sx={{ fontWeight: 'bold', display: 'block', mb: 0.5 }}>
                                    Top Dwell Activity:
                                  </Typography>
                                  {circle.dwellInsights.slice(0, 2).map((insight, idx) => {
                                    const hours = (insight.totalDwellTime / 3600).toFixed(1);
                                    return (
                                      <Typography key={idx} variant="caption" sx={{ fontSize: '0.7rem', display: 'block', ml: 1, mb: 0.3 }}>
                                        • <strong>{insight.entityId}</strong>: {insight.dwellCount} dwells, {hours}h total
                                      </Typography>
                          );
                        })}
                                </Box>
                              )}

                              {circle.coLocationInsights && circle.coLocationInsights.length > 0 && (
                                <Box sx={{ mb: 1 }}>
                                  <Typography variant="caption" sx={{ fontWeight: 'bold', display: 'block', mb: 0.5 }}>
                                    Top Co-Locations:
                          </Typography>
                                  {circle.coLocationInsights.slice(0, 2).map((coloc, idx) => {
                                    const duration = coloc.duration >= 3600 
                                      ? `${(coloc.duration / 3600).toFixed(1)}h`
                                      : `${(coloc.duration / 60).toFixed(0)}m`;
                                    return (
                                      <Typography key={idx} variant="caption" sx={{ fontSize: '0.7rem', display: 'block', ml: 1, mb: 0.3, wordBreak: 'break-word' }}>
                                        • {coloc.entities.join(' & ')} - {duration}
                                      </Typography>
                                    );
                                  })}
                                </Box>
                        )}
                      </Box>
                    )}

                          {/* Dwell Activity Tab */}
                          {(circleSubTabs[circle.id] || 0) === 1 && circle.dwellInsights && (
                            <Box>
                              {/* Export Button */}
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                                <Typography variant="subtitle2" sx={{ fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                  <Home sx={{ fontSize: 16 }} /> All Dwell Activity ({circle.dwellInsights.length})
                                </Typography>
                                <Button
                                  size="small"
                                  variant="outlined"
                                  startIcon={<PdfIcon />}
                                  onClick={() => exportDwellsPDF(circle)}
                                  sx={{ fontSize: '0.7rem', py: 0.5 }}
                                >
                                  Export
                                </Button>
                              </Box>
                              <Box sx={{ maxHeight: 400, overflow: 'auto' }}>
                              {circle.dwellInsights.map((insight, idx) => {
                                const hours = (insight.totalDwellTime / 3600).toFixed(1);
                                const minutesNum = Math.floor((insight.totalDwellTime % 3600) / 60);
                                const longestHours = (insight.longestDwell.duration / 3600).toFixed(1);
                                return (
                                  <Box key={idx} sx={{ mb: 1.5, p: 1, bgcolor: 'action.hover', borderRadius: 1 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 0.5 }}>
                                      <Typography variant="caption" sx={{ fontSize: '0.75rem', fontWeight: 'bold', display: 'block' }}>
                                        {insight.entityId} ({insight.deviceType})
                                      </Typography>
                                      <Tooltip title={savedDeviceIds.has(insight.entityId) ? 'Saved to devices' : 'Save this device'}>
                                        <span>
                                          <IconButton
                                            size="small"
                                            disabled={savingDeviceId === insight.entityId || savedDeviceIds.has(insight.entityId)}
                                            onClick={() => saveDeviceToWatchlist(insight.entityId)}
                                            sx={{ p: 0.25 }}
                                          >
                                            {savedDeviceIds.has(insight.entityId)
                                              ? <BookmarkIcon sx={{ fontSize: 15, color: 'primary.main' }} />
                                              : <BookmarkBorderIcon sx={{ fontSize: 15 }} />}
                                          </IconButton>
                                        </span>
                                      </Tooltip>
                                    </Box>
                                    
                                    {/* Location Name and Category */}
                                    {insight.placeName && (
                                      <Typography variant="caption" sx={{ fontSize: '0.75rem', display: 'flex', alignItems: 'center', gap: 0.5, color: 'primary.main', fontWeight: 'bold', mt: 0.5 }}>
                                        <LocationOn sx={{ fontSize: 14 }} /> {insight.placeName}
                                        {insight.placeType && insight.placeType !== 'unknown' && (
                                          <Chip 
                                            label={insight.placeType} 
                                            size="small" 
                                            sx={{ ml: 0.5, height: 16, fontSize: '0.65rem', bgcolor: 'primary.light', color: 'white' }} 
                                          />
                                        )}
                                      </Typography>
                                    )}
                                    
                                    <Typography variant="caption" sx={{ fontSize: '0.7rem', display: 'flex', alignItems: 'center', gap: 0.5, color: 'text.secondary' }}>
                                      <BarChart sx={{ fontSize: 12 }} /> {insight.dwellCount} dwells • <Timer sx={{ fontSize: 12, ml: 0.5 }} /> {hours}h {minutesNum > 0 ? `${minutesNum}m` : ''} total time
                                    </Typography>
                                    <Typography variant="caption" sx={{ fontSize: '0.7rem', display: 'block', color: 'text.secondary' }}>
                                      Longest: {longestHours}h ({insight.longestDwell.startTime.toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })})
                                    </Typography>
                                    <Typography variant="caption" sx={{ fontSize: '0.65rem', display: 'flex', alignItems: 'center', gap: 0.5, color: 'text.disabled' }}>
                                      <LocationOn sx={{ fontSize: 12 }} /> {insight.coordinates[0].toFixed(5)}, {insight.coordinates[1].toFixed(5)}
                                    </Typography>
                                  </Box>
                                );
                              })}
                              </Box>
                            </Box>
                          )}

                          {/* Co-Location Events Tab */}
                          {(circleSubTabs[circle.id] || 0) === 2 && circle.coLocationInsights && (
                            <Box>
                              {/* Export Button */}
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                                <Typography variant="subtitle2" sx={{ fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                  <Groups sx={{ fontSize: 16 }} /> All Co-Location Events ({circle.coLocationInsights.length})
                                </Typography>
                                <Button
                                  size="small"
                                  variant="outlined"
                                  startIcon={<PdfIcon />}
                                  onClick={() => exportCoLocationsPDF(circle)}
                                  sx={{ fontSize: '0.7rem', py: 0.5 }}
                                >
                                  Export
                                </Button>
                              </Box>
                              <Box sx={{ maxHeight: 400, overflow: 'auto' }}>
                              {circle.coLocationInsights.map((coloc, idx) => {
                                const duration = coloc.duration >= 3600 
                                  ? `${(coloc.duration / 3600).toFixed(1)}h`
                                  : `${(coloc.duration / 60).toFixed(0)}m`;
                                const confidence = (coloc.confidence * 100).toFixed(0);
                                return (
                                  <Box key={idx} sx={{ mb: 1.5, p: 1, bgcolor: 'action.hover', borderRadius: 1 }}>
                                    <Typography variant="caption" sx={{ fontSize: '0.75rem', fontWeight: 'bold', display: 'block', wordBreak: 'break-word' }}>
                                      {coloc.entities.join(' & ')}
                                    </Typography>
                                    
                                    {/* Location Name and Category */}
                                    {coloc.placeName && (
                                      <Typography variant="caption" sx={{ fontSize: '0.75rem', display: 'flex', alignItems: 'center', gap: 0.5, color: 'primary.main', fontWeight: 'bold', mt: 0.5 }}>
                                        <LocationOn sx={{ fontSize: 14 }} /> {coloc.placeName}
                                        {coloc.placeType && coloc.placeType !== 'unknown' && (
                                          <Chip 
                                            label={coloc.placeType} 
                                            size="small" 
                                            sx={{ ml: 0.5, height: 16, fontSize: '0.65rem', bgcolor: 'primary.light', color: 'white' }} 
                                          />
                                        )}
                                      </Typography>
                                    )}
                                    
                                    <Typography variant="caption" sx={{ fontSize: '0.7rem', display: 'flex', alignItems: 'center', gap: 0.5, color: 'text.secondary' }}>
                                      <Timer sx={{ fontSize: 12 }} /> {duration} overlap • {confidence}% confidence
                                    </Typography>
                                    <Typography variant="caption" sx={{ fontSize: '0.7rem', display: 'flex', alignItems: 'center', gap: 0.5, color: 'text.secondary' }}>
                                      <Timer sx={{ fontSize: 12 }} /> {coloc.overlapStart.toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })} - {coloc.overlapEnd.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                    </Typography>
                                    <Typography variant="caption" sx={{ fontSize: '0.65rem', display: 'flex', alignItems: 'center', gap: 0.5, color: 'text.disabled' }}>
                                      <LocationOn sx={{ fontSize: 12 }} /> {coloc.location[0].toFixed(5)}, {coloc.location[1].toFixed(5)}
                                    </Typography>
                                    <Box sx={{ mt: 0.5, display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                                      {coloc.deviceTypes.map((dt, dtIdx) => (
                                        <Chip key={dtIdx} label={dt} size="small" sx={{ height: 18, fontSize: '0.65rem' }} />
                                      ))}
                                    </Box>
                                  </Box>
                                );
                              })}
                              </Box>
                            </Box>
                          )}

                          {/* Signals Tab */}
                          {(circleSubTabs[circle.id] || 0) === 3 && circle.signalEnvironment && (
                            <Box>
                              <Typography variant="subtitle2" sx={{ fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: 0.5, mb: 1.5 }}>
                                <SignalsIcon sx={{ fontSize: 16 }} /> RF Signal Environment
                              </Typography>

                              {/* Signal summary chips */}
                              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mb: 2 }}>
                                {(circle.signalEnvironment.wifi?.length || 0) > 0 && (
                                  <Chip icon={<WifiIcon />} label={`${circle.signalEnvironment.wifi.length} WiFi APs`} size="small" color="primary" />
                                )}
                                {(circle.signalEnvironment.bluetooth?.length || 0) > 0 && (
                                  <Chip icon={<BluetoothIcon />} label={`${circle.signalEnvironment.bluetooth.length} BLE Devices`} size="small" color="info" />
                                )}
                                {(circle.signalEnvironment.cellular?.length || 0) > 0 && (
                                  <Chip icon={<CellTowerIcon />} label={`${circle.signalEnvironment.cellular.length} Cell Towers`} size="small" color="warning" />
                                )}
                                {'total_observations' in (circle.signalEnvironment.gnss || {}) && (circle.signalEnvironment.gnss as any).total_observations > 0 && (
                                  <Chip icon={<GnssIcon />} label={`${(circle.signalEnvironment.gnss as any).total_observations} GNSS Obs`} size="small" color="success" variant="outlined" />
                                )}
                              </Box>

                              <Box sx={{ maxHeight: 400, overflow: 'auto' }}>
                                {/* WiFi */}
                                {circle.signalEnvironment.wifi?.length > 0 && (
                                  <Box sx={{ mb: 2 }}>
                                    <Typography variant="caption" sx={{ fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5, color: 'primary.main' }}>
                                      <WifiIcon sx={{ fontSize: 14 }} /> WiFi Access Points ({circle.signalEnvironment.wifi.length})
                                    </Typography>
                                    {circle.signalEnvironment.wifi.slice(0, 20).map((w, idx) => (
                                      <Box key={idx} sx={{ mb: 0.75, p: 0.75, bgcolor: 'action.hover', borderRadius: 0.5 }}>
                                        <Typography variant="caption" sx={{ fontSize: '0.75rem', fontWeight: 'bold', display: 'block', fontFamily: 'monospace' }}>
                                          {w.bssid}
                                          {w.ssid && <span style={{ fontFamily: 'inherit', fontWeight: 'normal' }}> — {w.ssid}</span>}
                                        </Typography>
                                        <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'text.secondary', display: 'block' }}>
                                          {w.manufacturer || 'Unknown mfr'} • {w.observation_count} obs • {w.avg_signal_dbm != null ? `${w.avg_signal_dbm} dBm` : '—'}
                                          {w.frequency_mhz ? ` • ${w.frequency_mhz} MHz` : ''}
                                          {w.security ? ` • ${w.security}` : ''}
                                        </Typography>
                                      </Box>
                                    ))}
                                    {circle.signalEnvironment.wifi.length > 20 && (
                                      <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                                        ...and {circle.signalEnvironment.wifi.length - 20} more
                                      </Typography>
                                    )}
                                  </Box>
                                )}

                                {/* Bluetooth */}
                                {circle.signalEnvironment.bluetooth?.length > 0 && (
                                  <Box sx={{ mb: 2 }}>
                                    <Typography variant="caption" sx={{ fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5, color: 'info.main' }}>
                                      <BluetoothIcon sx={{ fontSize: 14 }} /> BLE Devices ({circle.signalEnvironment.bluetooth.length})
                                    </Typography>
                                    {circle.signalEnvironment.bluetooth.slice(0, 20).map((b, idx) => (
                                      <Box key={idx} sx={{ mb: 0.75, p: 0.75, bgcolor: 'action.hover', borderRadius: 0.5 }}>
                                        <Typography variant="caption" sx={{ fontSize: '0.75rem', fontWeight: 'bold', display: 'block' }}>
                                          {b.name || b.address}
                                          {b.name && b.name !== b.address && (
                                            <span style={{ fontWeight: 'normal', fontFamily: 'monospace', marginLeft: 4, fontSize: '0.7rem' }}>({b.address})</span>
                                          )}
                                        </Typography>
                                        <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'text.secondary', display: 'block' }}>
                                          {b.manufacturer || 'Unknown mfr'} • {b.observation_count} obs • {b.avg_rssi != null ? `${b.avg_rssi} dBm` : '—'}
                                          {b.bt_type ? ` • ${b.bt_type}` : ''}
                                        </Typography>
                                      </Box>
                                    ))}
                                    {circle.signalEnvironment.bluetooth.length > 20 && (
                                      <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                                        ...and {circle.signalEnvironment.bluetooth.length - 20} more
                                      </Typography>
                                    )}
                                  </Box>
                                )}

                                {/* Cellular */}
                                {circle.signalEnvironment.cellular?.length > 0 && (
                                  <Box sx={{ mb: 2 }}>
                                    <Typography variant="caption" sx={{ fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5, color: 'warning.main' }}>
                                      <CellTowerIcon sx={{ fontSize: 14 }} /> Cell Towers ({circle.signalEnvironment.cellular.length})
                                    </Typography>
                                    {circle.signalEnvironment.cellular.slice(0, 20).map((c, idx) => (
                                      <Box key={idx} sx={{ mb: 0.75, p: 0.75, bgcolor: 'action.hover', borderRadius: 0.5 }}>
                                        <Typography variant="caption" sx={{ fontSize: '0.75rem', fontWeight: 'bold', display: 'block', fontFamily: 'monospace' }}>
                                          {c.cell_tower_id}
                                          {c.operator && <span style={{ fontFamily: 'inherit', fontWeight: 'normal' }}> — {c.operator}</span>}
                                        </Typography>
                                        <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'text.secondary', display: 'block' }}>
                                          {c.network_type || '—'} • {c.observation_count} obs • {c.avg_signal_dbm != null ? `${c.avg_signal_dbm} dBm` : '—'}
                                          {c.mcc && c.mnc ? ` • MCC/MNC ${c.mcc}/${c.mnc}` : ''}
                                        </Typography>
                                      </Box>
                                    ))}
                                    {circle.signalEnvironment.cellular.length > 20 && (
                                      <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                                        ...and {circle.signalEnvironment.cellular.length - 20} more
                                      </Typography>
                                    )}
                                  </Box>
                                )}

                                {/* GNSS summary */}
                                {'total_observations' in (circle.signalEnvironment.gnss || {}) && (circle.signalEnvironment.gnss as any).total_observations > 0 && (
                                  <Box sx={{ mb: 1 }}>
                                    <Typography variant="caption" sx={{ fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5, color: 'success.main' }}>
                                      <GnssIcon sx={{ fontSize: 14 }} /> GNSS Health
                                    </Typography>
                                    <Box sx={{ p: 0.75, bgcolor: 'action.hover', borderRadius: 0.5 }}>
                                      <Typography variant="caption" sx={{ fontSize: '0.7rem', display: 'block' }}>
                                        {(circle.signalEnvironment.gnss as any).total_observations?.toLocaleString()} observations
                                        • {(circle.signalEnvironment.gnss as any).unique_svs} unique SVs
                                        • Fix rate: {(circle.signalEnvironment.gnss as any).fix_rate}%
                                        • Avg C/N0: {(circle.signalEnvironment.gnss as any).avg_cn0_dbhz ?? '—'} dB-Hz
                                      </Typography>
                                    </Box>
                                  </Box>
                                )}
                              </Box>
                            </Box>
                          )}

                          {/* AI Insights Tab */}
                          {(circleSubTabs[circle.id] || 0) === 4 && circle.aiSummary && (
                            <Box>
                              {/* Export Button */}
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                                <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                                  AI Insights
                                </Typography>
                                <Button
                                  size="small"
                                  variant="outlined"
                                  startIcon={<PdfIcon />}
                                  onClick={() => exportAIInsightsPDF(circle)}
                                  sx={{ fontSize: '0.7rem', py: 0.5 }}
                                >
                                  Export
                                </Button>
                              </Box>
                              <Box sx={{ maxHeight: 400, overflow: 'auto' }}>
                                <Typography variant="body2" sx={{ fontSize: '0.8rem', lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>
                                  {circle.aiSummary}
                                </Typography>
                              </Box>
                            </Box>
                          )}

                          {/* AdTech (Venntel) Tab */}
                          {adtechEnabled && (circleSubTabs[circle.id] || 0) === 5 && circle.adtechData && (
                            <Box>
                              <Typography variant="subtitle2" sx={{ fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: 0.5, mb: 1.5 }}>
                                <SignalsIcon sx={{ fontSize: 16, color: '#e91e63' }} /> AdTech Devices
                              </Typography>
                              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 1.5 }}>
                                <Chip
                                  label={`${circle.adtechData.unique_count} unique devices`}
                                  size="small"
                                  sx={{ bgcolor: 'rgba(233,30,99,0.15)', color: '#e91e63', fontWeight: 'bold' }}
                                />
                                <Chip
                                  label={`${circle.adtechData.total_signals} signals`}
                                  size="small"
                                  variant="outlined"
                                />
                              </Box>
                              {onLoadAdtechToMap && (() => {
                                const mappable = (circle.adtechData?.devices || [])
                                  .filter((d: any) => d && d.latitude != null && d.longitude != null)
                                  .map((d: any) => ({
                                    registration_id: d.registration_id || 'unknown',
                                    latitude: Number(d.latitude),
                                    longitude: Number(d.longitude),
                                    timestamp: d.timestamp ?? null,
                                    carrier: d.carrier ?? null,
                                    country: d.country ?? null,
                                    accuracy: d.accuracy ?? null,
                                    ip_address: d.ip_address ?? null,
                                    timezone: d.timezone ?? null,
                                    forensic_flag: d.forensic_flag ?? null,
                                  }));
                                return (
                                  <Button
                                    size="small"
                                    variant="contained"
                                    startIcon={<MyLocationIcon sx={{ fontSize: 16 }} />}
                                    disabled={mappable.length === 0}
                                    onClick={() => onLoadAdtechToMap(mappable, `Area: ${circle.name} (${mappable.length} signals)`)}
                                    sx={{ mb: 1, fontSize: '0.7rem', bgcolor: '#e91e63', '&:hover': { bgcolor: '#c2185b' } }}
                                  >
                                    Load {mappable.length} to map
                                  </Button>
                                );
                              })()}
                              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                                Commercial mobile-location observations in this area. Not persisted.
                              </Typography>
                              <Box sx={{ maxHeight: 360, overflow: 'auto' }}>
                                {(() => {
                                  // Group signals by registration_id for a device-level list.
                                  const byDevice = new Map<string, { count: number; last?: string; carrier?: string; country?: string }>();
                                  (circle.adtechData.devices || []).forEach((d: any) => {
                                    const rid = d.registration_id || 'unknown';
                                    const entry = byDevice.get(rid) || { count: 0 };
                                    entry.count += 1;
                                    if (!entry.last || (d.timestamp && d.timestamp > entry.last)) entry.last = d.timestamp;
                                    entry.carrier = entry.carrier || d.carrier;
                                    entry.country = entry.country || d.country;
                                    byDevice.set(rid, entry);
                                  });
                                  const rows = Array.from(byDevice.entries())
                                    .sort((a, b) => b[1].count - a[1].count)
                                    .slice(0, 100);
                                  return rows.map(([rid, info]) => (
                                    <Box
                                      key={rid}
                                      sx={{
                                        py: 0.5,
                                        borderBottom: '1px solid',
                                        borderColor: 'divider',
                                        display: 'flex',
                                        alignItems: 'flex-start',
                                        gap: 0.5,
                                      }}
                                    >
                                      <Box sx={{ flex: 1, minWidth: 0 }}>
                                        <Typography variant="caption" sx={{ fontFamily: 'monospace', display: 'block', wordBreak: 'break-all' }}>
                                          {rid}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">
                                          {info.count} obs{info.carrier ? ` · ${info.carrier}` : ''}{info.country ? ` · ${info.country}` : ''}
                                        </Typography>
                                      </Box>
                                      <AdtechDeviceActions
                                        registrationId={rid}
                                        courseId={selectedCourse}
                                        onLoadPol={onLoadAdtechPol}
                                        polLoading={adtechPolLoading}
                                      />
                                    </Box>
                                  ));
                                })()}
                              </Box>
                            </Box>
                          )}
                        </Box>
                      </Box>
                    )}

                    {!circle.dwellInsights && !circle.coLocationInsights && !circle.signalEnvironment && circle.results && circle.results.length === 0 && !circle.loading && (
                      <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                        No activity found in this area
                      </Typography>
                    )}
                  </Box>
                </ListItem>
              ))}
            </List>
          )}
        </CardContent>
      </Card>
      </Box>
      <PromoteToPacketDialog
        open={!!promoteTarget}
        onClose={() => { setPromoteTarget(null); setPromoteExtras([]); }}
        target={promoteTarget}
        extraMembers={promoteExtras}
        defaultName={promoteName}
      />
    </Box>
  );
};

export default AreaSearch;
